import { useCallback } from 'react';
import { useShellStore } from '@renderer/app/state/shell-store';

const messages = {
  'ro-RO': {
    navigation: {
      dashboard: 'Dashboard',
      projects: 'Proiecte',
      leads: 'Leaduri',
      tasks: 'Task-uri',
      customers: 'Clienti',
      resources: 'Resurse',
      contracts: 'Contracte',
      invoices: 'Facturi',
      payments: 'Plati',
      avi: 'AVI',
      settings: 'Setari',
      help: 'Ajutor'
    },
    shell: {
      searchPlaceholder: 'Cauta proiecte, leaduri, clienti, contracte, facturi, plati...',
      searchShortcut: 'CTRL + K',
      profile: 'Profil',
      settings: 'Setari',
      signOut: 'Deconectare',
      notification: 'Notificari',
      create: 'Adauga',
      delete: 'Sterge',
      trash: 'Cos',
      sync: 'Sincronizeaza',
      content: 'Continut'
    },
    trash: {
      title: 'Cos',
      loading: 'Se incarca elementele sterse...',
      emptyTitle: 'Cos gol',
      emptyDescription: 'Nu exista elemente sterse.',
      confirmDeleteForever: 'Stergi definitiv acest element?',
      confirmClearTrash: 'Stergi definitiv toate cele {count} elemente din cos?',
      confirmClearTab: 'Stergi definitiv cele {count} elemente din fila curenta?',
      actions: {
        restore: 'Restaureaza',
        deleteForever: 'Sterge definitiv',
        clearTrash: 'Golește coșul',
        clearTab: 'Golește fila'
      },
      filters: {
        all: 'Toate ({count})',
        projects: 'Proiecte ({count})',
        leads: 'Leaduri ({count})',
        tasks: 'Task-uri ({count})',
        customers: 'Clienti ({count})',
        contracts: 'Contracte ({count})',
        invoices: 'Facturi ({count})',
        payments: 'Plati ({count})'
      }
    },
    undo: {
      actions: {
        undo: 'Anuleaza'
      },
      toast: {
        availableTitle: 'Actiune salvata',
        availableMessage: 'Poti anula rapid ultima stergere sau modificare.',
        undoneTitle: 'Actiune anulata',
        undoneMessage: 'Ultima modificare a fost restaurata.',
        unavailableTitle: 'Nu se poate anula',
        unavailableMessage: 'Nu exista nicio actiune recenta de anulat.',
        failedTitle: 'Undo esuat',
        failedMessage: 'Actiunea nu a putut fi anulata.'
      }
    },
    search: {
      title: 'Cautare',
      inputPlaceholder: 'Cauta proiecte, leaduri, clienti, contracte, facturi si plati...',
      loading: 'Se cauta rezultate...',
      emptyTitle: 'Nu exista rezultate',
      emptyDescription: 'Introdu un proiect, lead, client, contract, factura sau plata pentru a continua.'
    },
    quickAdd: {
      title: 'Adauga: {domain}',
      inlineTitle: 'Adaugare rapida: {domain}',
      domain: 'Sectiune',
      shortcutHint: 'Ctrl/Cmd + Enter pentru salvare rapida',
      actions: {
        expandInline: 'Arata formularul rapid',
        collapseInline: 'Ascunde formularul rapid'
      },
      fields: {
        name: 'Nume',
        title: 'Titlu',
        description: 'Descriere',
        customer: 'Client',
        project: 'Proiect',
        category: 'Categorie',
        legalType: 'Forma juridica',
        dueAt: 'Termen',
        priority: 'Prioritate',
        mimeType: 'Tip (MIME)',
        storagePath: 'Cale (storage)',
        phone: 'Telefon',
        currentWebsite: 'Website actual',
        source: 'Sursa',
        tags: 'Etichete'
      },
      validation: {
        nameRequired: 'Completeaza numele.',
        titleRequired: 'Completeaza titlul.',
        customerRequired: 'Selecteaza un client.'
      }
    },
    common: {
      seeAll: 'Vezi toate',
      more: 'Mai multe',
      notSet: 'Neconfigurat',
      saved: 'Salvat',
      invalid: 'Invalid',
      noDeadline: 'Nu a fost stabilit un termen',
      noResults: 'Nu exista rezultate pentru filtrele curente.',
      openDetails: 'Vezi detalii',
      cancel: 'Anuleaza',
      send: 'Trimite',
      add: 'Adauga',
      remove: 'Sterge',
      yes: 'Da',
      no: 'Nu',
      close: 'Inchide',
      clear: 'Sterge',
      archived: 'Arhivat',
      clearSelection: 'Sterge selectia',
      applyChanges: 'Aplica modificari',
      leaveUnchanged: 'Lasa neschimbat',
      setValue: 'Seteaza',
      clearValue: 'Goleste',
      confirmDeleteSoft: 'Stergi acest element? Il poti restaura din Cos.',
      columns: {
        settingsTitle: 'Coloane afisate',
        settingsDescription: 'Alege coloanele vizibile pentru aceasta sectiune.',
        settingsReset: 'Reseteaza',
        settingsLocked: 'Fix',
        settingsButton: 'Setari coloane'
      },
      view: {
        list: 'Lista',
        grid: 'Grid'
      },
      languages: {
        'ro-RO': 'Romana',
        'en-US': 'English'
      }
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Privire de ansamblu asupra operatiunilor si livrarilor curente.',
      kpis: {
        activeProjects: 'Proiecte active',
        activeCustomers: 'Clienti activi',
        dueInvoices: 'Facturi scadente',
        activeContracts: 'Contracte'
      },
      panels: {
        recentActivity: 'Activitate recenta',
        financialOverview: 'Overview financiar',
        payments: 'Plati',
        activeTasks: 'Task-uri active',
        upcomingDeadlines: 'Termene apropiate',
        taskOverview: 'Overview task-uri'
      },
      activity: {
        project_created: '{actorName} a creat un nou proiect - {subject}',
        invoice_issued: '{actorName} a emis o factura pentru {subject}',
        contract_approved: '{actorName} a aprobat un contract cu {subject}',
        customer_added: '{actorName} a adaugat un nou client: {subject}',
        contract_review: '{actorName} a pornit revizuirea contractului pentru {subject}'
      },
      deadlineKinds: {
        contract: 'Contract',
        invoice: 'Factura',
        project: 'Proiect'
      },
      taskStatus: {
        backlog: 'Backlog',
        active: 'Active',
        complete: 'Finalizate',
        archived: 'Arhiva',
        removed: 'Sterse'
      },
      financial: {
        issuedThisMonth: 'Facturi emise in aceasta luna',
        collected: 'Incasat',
        outstanding: 'Neincasat'
      },
      payments: {
        pending: 'De incasat prin Stripe',
        pendingCount: '{count} in asteptare',
        paidCount: '{count} platite'
      }
    },
    projects: {
      title: 'Proiecte',
      subtitle: 'Suprafata principala pentru managementul si livrarea website-urilor.',
      summary: {
        active: 'Sunt {count} proiecte active',
        archived: '{count} sunt arhivate'
      },
      filters: {
        all: 'Toate',
        active: 'Active',
        in_progress: 'In lucru',
        pending: 'In asteptare',
        complete: 'Finalizate',
        archived: 'Arhivate',
        allCustomers: 'Toti clientii'
      },
      sort: {
        recent: 'Cele mai recente',
        deadline: 'Deadline apropiat',
        name: 'Nume A-Z'
      },
      view: {
        grid: 'Grid',
        list: 'Lista'
      },
      table: {
        name: 'Proiect',
        customer: 'Client',
        status: 'Status',
        progress: 'Progres',
        deadline: 'Termen'
      },
      card: {
        progress: 'Progres',
        launched: 'Lansat pe {date}',
        updated: 'Actualizat {value}',
        deadline: 'Deadline: {date}'
      },
      avi: {
        title: 'AVI',
        promptTitle: 'Creeaza un nou proiect pentru GreenSteps Solutions SRL. Fa un site WordPress.',
        response: 'Sigur, spune-mi domeniul si ce alte detalii mai pun in proiect?'
      },
      bulk: {
        title: '{count} proiecte selectate',
        clearValue: 'Goleste valoarea',
        setValue: 'Seteaza valoarea',
        assigneePlaceholder: 'Responsabil',
        tagsPlaceholder: 'eticheta 1, eticheta 2',
        fields: {
          status: 'Status',
          customer: 'Client',
          deadline: 'Deadline',
          assignee: 'Responsabil',
          tags: 'Etichete'
        }
      },
      placeholder: {
        title: 'Aceasta zona va fi extinsa dupa milestone 00.'
      }
    },
    leads: {
      title: 'Leaduri',
      quickAdd: {
        title: 'Adauga lead rapid',
        actions: {
          add: 'Adauga lead'
        },
        validation: {
          nameRequired: 'Completeaza numele leadului.'
        }
      },
      summary: {
        active: '{count} leaduri active',
        due: '{count} de contactat',
        converted: '{count} convertite'
      },
      filters: {
        all: 'Toate',
        status: 'Status lead',
        allCategories: 'Toate tipurile',
        unassignedCategory: 'Fara tip',
        categoryPlaceholder: 'Tip client',
        searchCategory: 'Cauta tip client',
        allCities: 'Toate orasele',
        missingCity: 'Fara oras',
        searchCity: 'Cauta oras',
        allFollowUps: 'Toate follow-up-urile',
        due: 'De contactat',
        scheduled: 'Programate',
        noFollowUp: 'Fara follow-up'
      },
      sort: {
        nextContact: 'Urmatorul contact',
        addedAt: 'Adaugat',
        recent: 'Cele mai recente',
        name: 'Nume A-Z',
        contact: 'Contact',
        category: 'Tip client',
        status: 'Status',
        website: 'Website',
        city: 'Oras'
      },
      view: {
        planner: 'Planificare'
      },
      planner: {
        title: 'Plan saptamanal',
        selectedDay: 'Zi selectata',
        smartFill: 'Planificare automata',
        weekStart: 'Saptamana',
        previousWeek: 'Saptamana anterioara',
        nextWeek: 'Saptamana urmatoare',
        thisWeek: 'Saptamana curenta',
        planned: '{count} planificate',
        contacted: '{count} contactate',
        emptyDayTitle: 'Nu sunt leaduri planificate',
        emptyDayDescription: 'Alege filtrele sau ruleaza planificarea automata pentru aceasta saptamana.',
        noCity: 'Fara oras',
        groupLabel: '{city} / {category}',
        selectedCount: '{count} selectate',
        assignSelected: 'Planifica selectia',
        clearSelected: 'Curata data',
        fields: {
          city: 'Oras',
          customerType: 'Tip client',
          startDate: 'Incepe din',
          dayCount: 'Zile',
          leadsPerDay: 'Leaduri / zi',
          statuses: 'Statusuri incluse'
        },
        actions: {
          smartFill: 'Genereaza plan',
          smartFilling: 'Se planifica...'
        },
        messages: {
          manualAssigned: '{count} leaduri planificate pentru {date}.',
          manualCleared: 'Data de contact a fost curatata pentru {count} leaduri.',
          smartFilled: '{planned} leaduri planificate in {days} zile. Sarite: {skipped}.'
        },
        badges: {
          contactedToday: 'Contactat azi'
        }
      },
      table: {
        name: 'Lead',
        contact: 'Contact',
        category: 'Tip client',
        addedAt: 'Adaugat',
        status: 'Status',
        nextContact: 'Urmatorul contact',
        website: 'Website',
        city: 'Oras'
      },
      card: {
        addedAt: 'Adaugat',
        dueNow: 'Acum: {date}',
        lastActivity: 'Ultima activitate',
        nextContact: 'Contact: {date}',
        noActivity: 'Fara contact inregistrat'
      },
      statusButtons: {
        ariaLabel: 'Status lead',
        current: 'Status curent: {status} pentru {lead}',
        openMenu: 'Schimba status pentru {lead}',
        menuLabel: 'Alege status lead',
        set: 'Seteaza {status} pentru {lead}'
      },
      selection: {
        title: '{count} leaduri selectate',
        description: 'Leadurile selectate pot fi mutate in Cos si restaurate ulterior.'
      },
      importExport: {
        tabs: {
          columns: 'Coloane',
          importExport: 'Import / Export'
        },
        import: {
          title: 'Import leaduri JSON',
          description: 'Lipeste JSON, trage fisierul aici sau lasa campul gol pentru import direct din clipboard.',
          pasteLabel: 'JSON pentru import',
          placeholder: 'Lipeste JSON aici sau apasa Importa JSON dupa ce ai copiat datele in clipboard.',
          dropTitle: 'Trage fisier JSON aici',
          dropSubtitle: 'Click pentru fisier sau foloseste campul de text.',
          fileReady: '{file} pregatit pentru import'
        },
        export: {
          title: 'Export leaduri',
          description: 'Exporta toate leadurile active intr-un fisier JSON reutilizabil.',
          dialogTitle: 'Exporta leaduri JSON'
        },
        demo: {
          title: 'Exemplu format JSON'
        },
        actions: {
          uploadJson: 'Alege fisier JSON',
          pasteJson: 'Lipeste JSON',
          copyDemo: 'Copiaza exemplu',
          preview: 'Previzualizeaza',
          previewing: 'Se verifica...',
          import: 'Importa JSON',
          importing: 'Se importa...',
          export: 'Salveaza export',
          exporting: 'Se exporta...',
          copyExport: 'Copiaza exportul',
          create: 'Nou',
          skip_existing: 'Existent',
          duplicate_in_file: 'Duplicat',
          invalid: 'Invalid'
        },
        summary: {
          new: 'noi',
          existing: 'existente',
          duplicates: 'duplicate',
          invalid: 'invalide',
          warnings: 'atentionari'
        },
        preview: {
          row: 'Rand {row}',
          match: 'Potrivire: {name}',
          warningCount: '{count} atentionari',
          ready: 'Pregatit pentru import'
        },
        reasons: {
          invalid_json: 'JSON invalid',
          invalid_format: 'Formatul trebuie sa fie un array sau un obiect cu leads.',
          invalid_schema_version: 'Versiune schema invalida',
          unsupported_schema_version: 'Versiune schema nesuportata',
          invalid_row: 'Randul nu este obiect JSON.',
          missing_name: 'Lipseste numele leadului.',
          invalid_fields: 'Un camp are format invalid.',
          invalid_customer_category: 'Tip client invalid.',
          customer_category_not_found: 'Tip client negasit.',
          duplicate_in_file: 'Exista deja in fisier.',
          existing_lead: 'Lead existent.',
          existing_deleted_lead: 'Lead gasit in Cos.'
        },
        messages: {
          demoCopied: 'Exemplul a fost copiat.',
          exportCopied: 'Exportul a fost copiat.',
          exportSaved: 'Fisierul JSON a fost salvat.',
          importComplete: '{count} leaduri importate.',
          imported: '{count} leaduri au fost adaugate.'
        },
        errors: {
          emptyJson: 'Lipeste JSON, alege un fisier sau copiaza JSON in clipboard.',
          previewFailed: 'Previzualizarea importului a esuat.',
          importFailed: 'Importul a esuat.',
          fileReadFailed: 'Fisierul JSON nu a putut fi citit.',
          exportFailed: 'Exportul a esuat.',
          copyFailed: 'Copierea a esuat.',
          clipboardUnavailable: 'Clipboardul nu este disponibil.'
        }
      },
      editor: {
        subtitleFallback: 'Lead pentru cold calling',
        tabs: {
          general: 'General',
          contact: 'Contactare',
          conversion: 'Conversie'
        },
        sections: {
          general: 'Detalii lead',
          logActivity: 'Inregistreaza contact',
          activityTimeline: 'Istoric contactare',
          conversion: 'Conversie in client si proiect'
        },
        panels: {
          nextAction: 'Urmatoarea actiune',
          lastActivity: 'Ultimul contact',
          contact: 'Date contact',
          conversion: 'Conversie'
        },
        fields: {
          name: 'Nume lead',
          status: 'Status',
          customerCategory: 'Tip client',
          companyName: 'Firma',
          contactName: 'Persoana contact',
          phone: 'Telefon',
          email: 'Email',
          currentWebsiteUrl: 'Website actual',
          city: 'Oras',
          source: 'Sursa',
          nextContactAt: 'Urmatorul contact',
          tags: 'Etichete',
          lostReason: 'Motiv pierdere',
          activityKind: 'Tip contact',
          activityOutcome: 'Rezultat',
          comment: 'Comentariu'
        },
        actions: {
          logContact: 'Log contact',
          saveActivity: 'Salveaza contact',
          convert: 'Creeaza client si proiect',
          viewConversion: 'Vezi conversia'
        },
        empty: {
          activities: 'Nu exista contacte sau comentarii pentru acest lead.'
        }
      },
      conversion: {
        newCustomer: 'Client nou',
        customer: 'Client',
        customerName: 'Nume client',
        projectName: 'Nume proiect',
        primaryDomain: 'Domeniu website'
      }
    },
    project: {
      tabs: {
        general: 'General',
        builder: 'Builder',
        tasks: 'Task-uri'
      },
      sections: {
        branding: 'Setari proiect si branding',
        social: 'Social media afisate in site',
        websiteControl: 'Control website',
        wordpressAdmin: 'Credențiale WordPress',
        summary: 'Sumar proiect',
        runtime: 'Runtime local',
        runtimeActivity: 'Activitate runtime',
        builder: 'Workspace Builder',
        tasks: 'Task-uri proiect'
      },
      fields: {
        projectName: 'Nume proiect',
        customer: 'Client asociat',
        status: 'Status proiect',
        summary: 'Sumar / descriere',
        logo: 'Logo website',
        icon: 'Favicon / icon',
        primaryDomain: 'Website principal',
        localDomain: 'Local',
        runtimeStatus: 'Status runtime',
        siteRootPath: 'Folder site',
        documentRootPath: 'Document root',
        databaseName: 'Baza de date',
        databaseTablePrefix: 'Prefix tabele',
        httpsEnabled: 'HTTPS',
        lastUpdatedAt: 'Ultima actualizare',
        builderRevision: 'Revizie builder',
        runtimeOperation: 'Operatiune runtime',
        runtimeStep: 'Pas curent',
        runtimeStartedAt: 'Pornit la',
        runtimeFinishedAt: 'Finalizat la',
        wordpressAdminUsername: 'User admin WordPress',
        wordpressAdminPassword: 'Parola admin WordPress'
      },
      actions: {
        builder: 'Builder',
        update: 'Update',
        installSite: 'Instaleaza site',
        updateSite: 'Actualizeaza site',
        installingSite: 'Se instaleaza...',
        updatingSite: 'Se actualizeaza...',
        switchType: 'Schimba runtime',
        uploadLogo: 'Incarca SVG logo',
        uploadIcon: 'Incarca SVG icon',
        openResources: 'Resurse / Media',
        openPrimaryDomain: 'Deschide website',
        openLocalDomain: 'Deschide local',
        openBuilder: 'Deschide builder',
        saveCredentials: 'Salvează',
        clearProjectPassword: 'Șterge parola proiectului'
      },
      branding: {
        emptyAsset: 'Niciun SVG salvat'
      },
      summary: {
        status: 'Status',
        stage: 'Etapa',
        deployment: 'Deployment',
        progress: 'Progres',
        runtimeProgress: 'Progres runtime',
        recentChanges: 'Schimbari recente',
        runtimeMessage: 'Mesaj runtime',
        wordpressAdminDefaultUsername: 'User implicit',
        wordpressAdminProjectPassword: 'Parola proiect',
        wordpressAdminDefaultPassword: 'Parola implicita'
      },
      runtime: {
        idle: 'Site-ul local nu este inca provisionat.',
        ready: 'Site-ul local este pregatit pentru lucru.',
        warning: 'Runtime-ul local are nevoie de atentie.',
        updating: 'Runtime-ul local se actualizeaza.',
        notProvisioned: 'Nu exista runtime local pentru acest proiect.',
        enabled: 'Activ',
        disabled: 'Inactiv',
        noDatabase: 'Nu este necesara pentru acest runtime.',
        operationKinds: {
          provision: 'Provisionare',
          update: 'Actualizare',
          verify: 'Verificare'
        },
        steps: {
          prepareContext: 'Pregatim runtime-ul local',
          resetSiteRoot: 'Resetam folderul site-ului',
          generateBuilderArtifacts: 'Generam artefactele builder-ului',
          prepareWordPressPackage: 'Pregatim pachetul WordPress',
          extractWordPressCore: 'Extragem fisierele WordPress',
          syncWordPressCore: 'Copiem fisierele WordPress',
          createDatabase: 'Cream baza de date',
          writeWordPressConfig: 'Scriem configuratia WordPress',
          cleanupWordPressDefaults: 'Curatam fisierele implicite WordPress',
          installWordPressStarter: 'Instalam starterul Alttvision',
          runWordPressInstaller: 'Rulam instalatorul WordPress',
          verifyWordPressRuntime: 'Verificam runtime-ul WordPress',
          prepareUpdate: 'Pregatim actualizarea runtime-ului',
          updatePhpRuntime: 'Actualizam runtime-ul PHP',
          verifyHttps: 'Verificam HTTPS',
          writeApacheBindings: 'Scriem configuratia Apache',
          reloadApache: 'Repornim Apache',
          verifyHttpsProbe: 'Verificam raspunsul local HTTPS',
          regenerateCertificate: 'Regeneram certificatul SSL'
        }
      },
      notifications: {
        saveSuccessTitle: 'Proiect salvat',
        saveSuccessMessage: 'Modificarile proiectului au fost salvate local.',
        saveErrorTitle: 'Salvarea proiectului a esuat',
        credentialsSavedTitle: 'Credențiale salvate',
        credentialsSavedMessage: 'Credențialele WordPress au fost salvate local.',
        credentialsSaveErrorTitle: 'Salvarea credențialelor a eșuat',
        installStartedTitle: 'Instalare pornita',
        installStartedMessage: 'Provisionarea website-ului local a inceput.',
        installSuccessTitle: 'Site instalat',
        installWarningTitle: 'Instalare cu avertismente',
        installErrorTitle: 'Instalarea site-ului a esuat',
        updateStartedTitle: 'Actualizare pornita',
        updateStartedMessage: 'Actualizarea website-ului local a inceput.',
        updateSuccessTitle: 'Site actualizat',
        updateWarningTitle: 'Actualizare cu avertismente',
        updateErrorTitle: 'Actualizarea site-ului a esuat',
        openLocalErrorTitle: 'Website local indisponibil',
        brandingUploadSuccessTitle: 'Branding actualizat',
        brandingUploadSuccessMessageLogo: 'Logo-ul proiectului a fost actualizat.',
        brandingUploadSuccessMessageIcon: 'Iconita proiectului a fost actualizata.',
        brandingUploadErrorTitle: 'Incarcarea brandingului a esuat',
        brandingUploadInvalidFile: 'Trebuie sa alegi un fisier SVG.',
        taskSavedTitle: 'Task salvat',
        taskCreatedMessage: 'Task-ul a fost adaugat proiectului.',
        taskUpdatedMessage: 'Task-ul proiectului a fost actualizat.',
        taskDeletedTitle: 'Task sters',
        taskDeletedMessage: 'Task-ul a fost mutat in Cos.',
        taskDeleteErrorTitle: 'Stergerea task-ului a esuat'
      },
      timeline: {
        branding_updated: 'Branding actualizat',
        content_synced: 'Continut sincronizat',
        builder_synced: 'Builder sincronizat',
        update_ready: 'Update pregatit'
      },
      builder: {
        empty: {
          title: 'Builder pregatit pentru prima pagina',
          description: 'Creeaza prima pagina si construieste site-ul doar din sectiuni simple, stabile si usor de exportat.',
          createFirstPage: 'Creeaza prima pagina'
        },
        toast: {
          title: 'Builder',
          saveFailed: 'Pagina din builder nu a putut fi salvata.',
          createFailed: 'Pagina noua nu a putut fi creata.',
          deleteFailed: 'Pagina nu a putut fi stearsa.',
          primaryFailed: 'Pagina principala nu a putut fi actualizata.'
        },
        tabs: {
          content: 'Continut',
          pages: 'Pagini',
          seo: 'SEO',
          theme: 'Stil'
        },
        page: {
          primarySuffix: 'homepage',
          primaryBadge: 'Pagina principala',
          newPage: 'Pagina noua',
          setPrimary: 'Seteaza homepage',
          delete: 'Sterge pagina'
        },
        pages: {
          title: 'Pagini website',
          count: '{count} pagini',
          settingsTitle: 'Setari pagina',
          primaryStatus: 'Rol pagina',
          secondaryPage: 'Pagina secundara',
          sectionCountLabel: 'Sectiuni active'
        },
        status: {
          valid: 'Valid',
          errors: '{count} erori',
          warnings: '{count} avertismente',
          saving: 'Se salveaza...',
          unsaved: 'Modificari nesalvate',
          saved: 'Salvat local'
        },
        structure: {
          title: 'Structura pagina',
          sectionCount: '{count} sectiuni',
          addSection: 'Adauga sectiune',
          emptyTitle: 'Nicio sectiune',
          emptyDescription: 'Adauga prima sectiune aprobata pentru a incepe structura paginii.'
        },
        preview: {
          title: 'Preview live',
          emptyTitle: 'Pagina indisponibila',
          emptyDescription: 'Selecteaza sau creeaza o pagina pentru preview.',
          devices: {
            desktop: 'Desktop',
            tablet: 'Tablet',
            mobile: 'Mobil'
          }
        },
        inspector: {
          pageTitle: 'Setari pagina',
          sectionTitle: 'Setari sectiune',
          emptyTitle: 'Alege o sectiune',
          emptyDescription: 'Selecteaza o sectiune din structura sau din preview pentru a edita continutul.'
        },
        seo: {
          title: 'SEO si meta',
          previewTitle: 'Preview snippet',
          previewDescription: 'Titlul si descrierea sunt pregatite pentru export si viitoarele fluxuri AI.',
          previewFallback: 'Adauga o descriere scurta pentru a clarifica intentia si beneficiile paginii.'
        },
        theme: {
          title: 'Stil si theme preset',
          description: 'Selecteaza un preset stabil pe proiect pentru layout, ton si viitoare generari.',
          currentTitle: 'Preset selectat',
          placeholder: 'Presetul este memorat la nivel de proiect. In aceasta etapa definim selectia si brandingul, fara rescriere automata de continut.',
          noBrandingAsset: 'Niciun asset selectat',
          updateFailed: 'Presetul nu a putut fi actualizat.'
        },
        themePresets: {
          real_estate: {
            label: 'Real Estate',
            description: 'Vitrine imobiliare, lead forms si prezentare de proprietati.'
          },
          dental_clinic: {
            label: 'Dental Clinic',
            description: 'Claritate medicala, servicii si incredere institutionala.'
          },
          beauty_clinic: {
            label: 'Beauty Clinic',
            description: 'Look editorial pentru proceduri, pachete si conversii premium.'
          },
          restaurant: {
            label: 'Restaurant',
            description: 'Meniu, atmosfera si rezervari cu accent pe storytelling vizual.'
          },
          corporate_services: {
            label: 'Corporate Services',
            description: 'Presetul neutru pentru agentii, firme B2B si site-uri de prezentare.'
          }
        },
        picker: {
          title: 'Biblioteca sectiuni',
          close: 'Inchide'
        },
        dialogs: {
          create: {
            title: 'Pagina noua',
            description: 'Seteaza structura initiala pentru o pagina simpla, exportabila in PHP si WordPress.',
            submit: 'Creeaza pagina',
            validation: {
              titleRequired: 'Completeaza titlul paginii.'
            },
            defaults: {
              homepageTitle: 'Homepage',
              standardTitle: 'Pagina noua'
            }
          },
          delete: {
            title: 'Sterge pagina',
            description: 'Pagina "{title}" va fi scoasa din builder si din runtime-ul sincronizat.',
            submit: 'Sterge pagina'
          }
        },
        fields: {
          pageTitle: 'Titlu pagina',
          slug: 'Slug',
          pageType: 'Tip pagina',
          status: 'Status',
          seoTitle: 'SEO title',
          seoDescription: 'SEO description',
          tone: 'Tone',
          eyebrow: 'Eyebrow',
          heading: 'Heading',
          subheading: 'Subheading',
          alignment: 'Aliniere',
          primaryCta: 'Primary CTA',
          secondaryCta: 'Secondary CTA',
          media: 'Media',
          intro: 'Intro',
          body: 'Body',
          title: 'Titlu',
          question: 'Intrebare',
          value: 'Valoare',
          quote: 'Quote',
          caption: 'Caption',
          label: 'Label',
          author: 'Autor',
          role: 'Rol',
          description: 'Descriere',
          answer: 'Raspuns',
          image: 'Imagine',
          icon: 'Icon',
          address: 'Adresa',
          phone: 'Telefon',
          email: 'Email',
          mapLabel: 'Map label',
          companyName: 'Nume companie',
          tagline: 'Tagline',
          link: 'Link',
          text: 'Text',
          style: 'Stil',
          altText: 'Alt text',
          menuLocation: 'Locatie meniu'
        },
        menuLocations: {
          none: 'Nu afisa in meniu',
          primary: 'Header principal',
          footer: 'Footer'
        },
        actions: {
          maximizeWorkspace: 'Maximizeaza builder-ul',
          restoreWorkspace: 'Micsoreaza builder-ul',
          moveUp: 'Sus',
          moveDown: 'Jos',
          duplicate: 'Duplica',
          hide: 'Ascunde',
          show: 'Afiseaza',
          enable: 'Activeaza',
          deleteSection: 'Sterge sectiunea',
          addHighlight: 'Adauga highlight',
          deleteHighlight: 'Sterge highlight',
          addItem: 'Adauga item',
          deleteItem: 'Sterge item',
          addLink: 'Adauga link',
          deleteLink: 'Sterge link'
        },
        alignments: {
          start: 'Start',
          center: 'Center',
          end: 'End'
        },
        tones: {
          default: 'Default',
          brand: 'Brand',
          muted: 'Muted',
          contrast: 'Contrast'
        },
        buttonStyles: {
          primary: 'Primary',
          secondary: 'Secondary'
        },
        assets: {
          none: 'Fara asset',
          selected: 'Asset selectat',
          icon: 'Icon'
        },
        contact: {
          staticHint: 'Formularul nu este activ in v1. Sectiunea exporta doar date de contact si link-uri.'
        },
        defaults: {
          highlight: 'Highlight nou'
        },
        validation: {
          error: 'Eroare',
          warning: 'Avertisment'
        },
        pageTypes: {
          homepage: {
            label: 'Homepage',
            description: 'Pagina principala cu structura de prezentare si CTA clar.'
          },
          standard: {
            label: 'Pagina standard',
            description: 'Pagina simpla pentru continut, servicii sau prezentare.'
          },
          contact: {
            label: 'Contact',
            description: 'Pagina cu date de contact, informatii utile si CTA static.'
          }
        },
        variants: {
          split: 'Split',
          default: 'Default',
          cards: 'Cards',
          grid: 'Grid',
          numbered: 'Numbered',
          band: 'Band',
          accordion: 'Accordion',
          banner: 'Banner',
          'details-form': 'Details',
          simple: 'Simple'
        },
        sectionCatalog: {
          hero: {
            label: 'Hero',
            description: 'Titlu principal, text scurt si actiuni clare.'
          },
          about: {
            label: 'Despre',
            description: 'Context, introducere si puncte cheie despre brand sau pagina.'
          },
          services: {
            label: 'Servicii',
            description: 'Lista de servicii de baza in carduri compacte.'
          },
          features: {
            label: 'Avantaje',
            description: 'Beneficii sau diferentiatori structurati simplu.'
          },
          steps: {
            label: 'Pasi',
            description: 'Proces clar, in pasi numerotati.'
          },
          stats: {
            label: 'Statistici',
            description: 'Cifre scurte si usor de scanat.'
          },
          testimonials: {
            label: 'Testimoniale',
            description: 'Citari scurte de la clienti sau parteneri.'
          },
          faq: {
            label: 'FAQ',
            description: 'Intrebari frecvente cu raspunsuri concise.'
          },
          cta: {
            label: 'CTA',
            description: 'Bloc de conversie cu doua actiuni maxime.'
          },
          contact: {
            label: 'Contact',
            description: 'Date de contact statice, fara backend de formular.'
          },
          gallery: {
            label: 'Galerie',
            description: 'Grila simpla de imagini cu caption optional.'
          },
          footer: {
            label: 'Footer',
            description: 'Inchidere de pagina cu link-uri si date de contact.'
          }
        },
        ai: {
          bubble: {
            open: 'Asistent AI',
            close: 'Inchide asistentul'
          },
          title: 'Asistent AI builder',
          subtitle: 'Genereaza si ajusteaza pagini folosind biblioteca de sectiuni.',
          modes: {
            fullDesign: 'Design complet',
            editSection: 'Modifica sectiune',
            copyPolish: 'Rafineaza text'
          },
          modeHints: {
            fullDesign: 'Regenereaza toata pagina folosind brand-ul si prompt-ul tau.',
            editSection: 'Rescrie sectiunea selectata pe baza instructiunilor.',
            copyPolish: 'Rafineaza textul sectiunii curente fara a schimba structura.'
          },
          context: {
            activePage: 'Pagina: {title}',
            activeSection: 'Sectiune: {label}',
            noSection: 'Selecteaza o sectiune din structura pentru modificare.',
            noPage: 'Selecteaza sau creeaza o pagina pentru a folosi AI.'
          },
          prompt: {
            label: 'Prompt',
            placeholder: 'Descrie in romana cum vrei sa arate pagina sau sectiunea...',
            exampleHint: 'Sfat: mentioneaza tipul de business, atmosfera si CTA-urile dorite.'
          },
          actions: {
            generate: 'Genereaza',
            generating: 'Se genereaza...',
            apply: 'Aplica modificarile',
            discard: 'Renunta',
            undo: 'Anuleaza modificarea',
            retry: 'Incearca din nou',
            clearCache: 'Curata cache-ul'
          },
          status: {
            idle: 'Pregatit',
            loading: 'AI lucreaza la rezultat...',
            ready: 'Propunere gata pentru aplicare',
            applied: 'Modificarile au fost aplicate.',
            undone: 'Modificarea a fost anulata.',
            cached: 'Reutilizat din cache'
          },
          diff: {
            title: 'Propunere AI',
            sections: '{count} sectiuni',
            newLabel: 'Nou',
            changedLabel: 'Modificat'
          },
          errors: {
            noKey: 'Adauga cheia API din Setari > AI pentru a folosi aceasta functie.',
            noSection: 'Selecteaza o sectiune pentru acest mod.',
            noPage: 'Selecteaza o pagina pentru a folosi AI.',
            generic: 'Generarea a esuat. Incearca din nou sau schimba prompt-ul.',
            invalidJson: 'Raspunsul AI nu a respectat schema. Incearca alt prompt.'
          },
          model: {
            label: 'Model',
            provider: {
              anthropic: 'Anthropic',
              openai: 'OpenAI'
            }
          }
        }
      },
      tasks: {
        title: 'Nu exista task-uri pentru acest proiect',
        description: 'Adauga primul task pentru a urmari lucrul direct din proiect.',
        actions: {
          add: 'Adauga task'
        }
      },
      social: {
        facebook: 'Facebook',
        instagram: 'Instagram',
        googleBusiness: 'Google Business',
        youtube: 'YouTube'
      }
    },
    customers: {
      title: 'Clienti',
      summary: {
        active: 'Sunt {count} clienti activi',
        archived: '{count} sunt arhivati'
      },
      filters: {
        all: 'Toti',
        active: 'Activi',
        inactive: 'Inactivi',
        lead: 'Lead-uri',
        archived: 'Arhivati',
        allTypes: 'Toate tipurile',
        allCategories: 'Toate tipurile',
        unassignedCategory: 'Fara tip',
        categoryPlaceholder: 'Tip client',
        searchCategory: 'Cauta tip client'
      },
      sort: {
        recent: 'Cei mai recenti',
        name: 'Nume A-Z'
      },
      table: {
        name: 'Nume',
        type: 'Tip client',
        category: 'Tip client',
        contact: 'Persoana de contact',
        phone: 'Numar de telefon',
        noContact: 'Fara contact principal',
        noPhone: 'Fara numar'
      },
      editor: {
        tabs: {
          general: 'General',
          projects: 'Proiecte',
          contracts: 'Contracte',
          invoices: 'Facturi'
        },
        hero: {
          favorite: 'Favorit'
        },
        site: {
          live: 'Site live',
          local: 'Site local'
        },
        sections: {
          general: 'Date client',
          primaryContact: 'Contact principal',
          socialMedia: 'Social media',
          projects: 'Proiecte',
          contracts: 'Contracte',
          invoices: 'Facturi',
          contactAddress: 'Date de contact si adrese',
          notes: 'Notite'
        },
        fields: {
          name: 'Nume client',
          status: 'Status',
          legalName: 'Nume firma',
          cui: 'CIF/CUI',
          registrationNumber: 'Nr. Reg. Com',
          vatPayer: 'Platitor TVA',
          email: 'Email',
          website: 'Website',
          customerType: 'Forma juridica',
          customerCategory: 'Tip client',
          legalType: 'Forma juridica',
          shortDescription: 'Scurta descriere'
        },
        summary: {
          activeSince: 'Client activ din {value}',
          activeSinceUnknown: 'Client activ',
          completedProjects: '{count} proiecte finalizate',
          generatedRevenue: 'Venit generat'
        },
        actions: {
          addNote: 'Adauga notita',
          addAddress: 'Adauga adresa'
        },
        projects: {
          columns: {
            project: 'Proiect',
            status: 'Status',
            type: 'Tip',
            updated: 'Actualizat'
          },
          updated: 'Actualizat {date}'
        },
        contracts: {
          expires: 'Expira {date}',
          noEndDate: 'Fara data finala'
        },
        invoices: {
          due: 'Scadenta {date}'
        },
        empty: {
          contact: 'Contactul principal va fi definit intr-un milestone urmator.',
          socialMedia: 'Suprafata social media ramane rezervata pentru milestone-ul urmator.',
          projects: 'Nu exista proiecte asociate acestui client.',
          contracts: 'Nu exista contracte asociate acestui client.',
          invoices: 'Nu exista facturi asociate acestui client.',
          notes: 'Nu exista notite pentru acest client.'
        }
      },
      selection: {
        title: '{count} clienti selectati',
        description: 'Editarea multipla pentru clienti ramane rezervata pana cand contractele de mutatie sunt definite.'
      }
    },
    contracts: {
      title: 'Contracte',
      summary: {
        active: '{count} contracte active',
        archived: '{count} arhivate',
        effectiveFrom: 'De la',
        effectiveUntil: 'Pana la',
        value: 'Valoare',
        signature: 'Semnare',
        issuer: 'Emitent',
        customer: 'Client',
        generated: 'Generat'
      },
      filters: {
        all: 'Toate',
        draft: 'Draft',
        pending_review: 'In review',
        ready_to_send: 'Pregatite',
        signed: 'Semnate',
        archived: 'Arhivate',
        allCustomers: 'Toti clientii',
        allProjects: 'Toate proiectele'
      },
      sort: {
        recent: 'Actualizate recent',
        effective: 'Expira curand',
        title: 'Titlu A-Z'
      },
      table: {
        title: 'Contract',
        customer: 'Client',
        project: 'Proiect',
        value: 'Valoare',
        effectiveUntil: 'Valabil pana la {date}',
        noEndDate: 'Fara data finala'
      },
      selection: {
        title: '{count} contracte selectate',
        description: 'Selectia multipla ramane disponibila doar pentru context si revizuire in milestone 05.'
      },
      actions: {
        openTemplates: 'Template-uri contract'
      },
      tabs: {
        general: 'General',
        document: 'Document',
        activity: 'Activitate'
      },
      sections: {
        summary: 'Sumar contract',
        general: 'Date generale',
        parties: 'Parti',
        terms: 'Termeni',
        financials: 'Financiar',
        document: 'Document',
        activity: 'Activitate',
        documentSummary: 'Rezumat document'
      },
      fields: {
        title: 'Titlu contract',
        contractNumber: 'Numar contract',
        status: 'Status',
        template: 'Template',
        customer: 'Client',
        project: 'Proiect',
        effectiveFrom: 'Activ de la',
        effectiveUntil: 'Activ pana la',
        scope: 'Scope servicii',
        renewalNotes: 'Note reinnoire',
        terminationNotes: 'Note incetare',
        value: 'Valoare',
        currency: 'Moneda',
        paymentNotes: 'Note plata',
        scheduleSummary: 'Sumar calendar'
      },
      parties: {
        issuer: 'Emitent',
        customer: 'Client'
      },
      document: {
        templateVersion: 'Versiune template',
        generatedAt: 'Generat la',
        signatureStatus: 'Status semnare',
        sourceFormat: 'Format sursa',
        pdfAsset: 'PDF atasat',
        resolvedBody: 'Document rezolvat'
      },
      empty: {
        activity: 'Nu exista activitate disponibila pentru acest contract.'
      },
      activityKinds: {
        created: 'Creat',
        generated: 'Generat',
        sent: 'Trimis',
        signed: 'Semnat',
        updated: 'Actualizat'
      },
      templates: {
        title: 'Template-uri contract',
        subtitle: 'Blueprint-uri versionate pentru contracte romanesti si generare controlata de documente.',
        newTitle: 'Template nou',
        summary: {
          active: '{count} active',
          draft: '{count} draft',
          archived: '{count} arhivate'
        },
        actions: {
          backToContracts: 'Inapoi la contracte',
          new: 'Template nou',
          saveTemplate: 'Salveaza template-ul',
          addPlaceholder: 'Adauga placeholder',
          removePlaceholder: 'Sterge',
          refreshPreview: 'Actualizeaza preview',
          generateContract: 'Genereaza contract'
        },
        filters: {
          all: 'Toate',
          draft: 'Draft',
          active: 'Active',
          archived: 'Arhivate',
          allCategories: 'Toate categoriile',
          allLanguages: 'Toate limbile'
        },
        sort: {
          updated: 'Actualizate recent',
          name: 'Nume A-Z',
          version: 'Versiune curenta'
        },
        table: {
          template: 'Template',
          category: 'Categorie',
          issuer: 'Emitent implicit',
          version: 'Versiune'
        },
        selection: {
          title: '{count} template-uri selectate',
          description: 'Selectia ramane disponibila pentru consistenta de colectie si revizuire rapida.'
        },
        tabs: {
          general: 'General',
          body: 'Body',
          placeholders: 'Placeholders',
          outputRules: 'Reguli output',
          versions: 'Versiuni'
        },
        sections: {
          summary: 'Rezumat template',
          general: 'Date generale',
          body: 'Body template',
          placeholders: 'Schema placeholders',
          outputRules: 'Reguli output',
          versions: 'Istoric versiuni',
          generation: 'Generare contract',
          manualTokens: 'Valori manuale',
          preview: 'Preview document'
        },
        fields: {
          name: 'Nume template',
          slug: 'Slug',
          category: 'Categorie',
          language: 'Limba',
          status: 'Status',
          defaultIssuer: 'Emitent implicit',
          notes: 'Note interne',
          bodySource: 'Continut markdown',
          numberingPrefix: 'Prefix numerotare',
          signatureFooter: 'Footer semnare',
          changeSummary: 'Rezumat schimbari',
          required: 'Obligatoriu',
          customer: 'Client',
          project: 'Proiect',
          issuer: 'Emitent',
          contractTitle: 'Titlu contract',
          effectiveFrom: 'Activ de la',
          effectiveUntil: 'Activ pana la',
          totalValue: 'Valoare totala',
          currency: 'Moneda',
          scopeSummary: 'Scope servicii',
          paymentNotes: 'Note plata',
          scheduleSummary: 'Calendar livrare',
          renewalNotes: 'Note reinnoire',
          terminationNotes: 'Note incetare'
        },
        summaryFields: {
          slug: 'Slug',
          placeholders: 'Placeholders',
          manualTokens: 'Token-uri manuale',
          previewStatus: 'Status preview'
        },
        placeholderColumns: {
          key: 'Cheie',
          meta: 'Sursa si tip',
          label: 'Label key',
          fallback: 'Fallback'
        },
        groups: {
          customer: 'Client',
          issuer: 'Emitent',
          project: 'Proiect',
          contract: 'Contract',
          manual: 'Manual'
        },
        sources: {
          customer: 'Date client',
          issuer: 'Date emitent',
          project: 'Date proiect',
          contract: 'Date contract',
          manual: 'Valoare manuala'
        },
        valueTypes: {
          string: 'Text',
          multiline: 'Text lung',
          currency: 'Valoare',
          date: 'Data'
        },
        messages: {
          templateSaved: 'Template-ul a fost salvat.',
          saveToPreview: 'Salveaza modificarile template-ului inainte de preview sau generare.'
        },
        empty: {
          placeholders: 'Adauga primul placeholder pentru a construi schema template-ului.',
          versions: 'Nu exista versiuni salvate pentru acest template.'
        },
        preview: {
          pending: 'In asteptarea preview-ului',
          ready: 'Preview actualizat',
          saveRequired: 'Salveaza template-ul pentru a activa preview-ul si generarea.',
          empty: 'Completeaza contextul si actualizeaza preview-ul pentru a rezolva documentul.',
          contractNumber: 'Numar contract',
          contractTitle: 'Titlu contract',
          currency: 'Moneda',
          missingTokens: 'Token-uri lipsa',
          customerSnapshot: 'Snapshot client',
          issuerSnapshot: 'Snapshot emitent',
          body: 'Body rezolvat'
        },
        versions: {
          versionLabel: 'v{version}',
          current: 'Curenta',
          none: 'Fara versiune',
          noChangeSummary: 'Fara rezumat schimbari'
        },
        placeholders: {
          issuerLegalName: 'Denumire legala emitent',
          issuerIban: 'IBAN emitent',
          customerLegalName: 'Denumire legala client',
          customerRegistrationNumber: 'Nr. Reg. Com. client',
          customerBillingAddress: 'Adresa facturare client',
          customerCui: 'CUI client',
          customerSignatoryName: 'Reprezentant client',
          projectName: 'Nume proiect',
          projectPrimaryDomain: 'Domeniu principal proiect',
          contractNumber: 'Numar contract',
          contractIssueDate: 'Data emiterii',
          contractCurrency: 'Moneda contract',
          contractTotalValue: 'Valoare contract',
          contractScopeSummary: 'Scope servicii',
          contractEffectiveFrom: 'Data inceperii',
          contractPaymentNotes: 'Note plata',
          contractScheduleSummary: 'Calendar livrare',
          contractTerminationNotes: 'Note incetare'
        }
      }
    },
    invoices: {
      title: 'Facturi',
      summary: {
        active: '{count} facturi active',
        archived: '{count} arhivate',
        issueDate: 'Emisa',
        dueDate: 'Scadenta',
        total: 'Total',
        sync: 'Sync',
        subtotal: 'Subtotal',
        tax: 'TVA'
      },
      filters: {
        all: 'Toate',
        draft: 'Draft',
        issued: 'Emise',
        paid: 'Platite',
        overdue: 'Intarziate',
        archived: 'Arhivate',
        allCustomers: 'Toti clientii',
        allIssuers: 'Toti emitentii',
        allDueStates: 'Toate scadentele',
        open: 'Deschise',
        overdueState: 'Intarziate',
        paidState: 'Platite'
      },
      sort: {
        issueDate: 'Data emiterii',
        dueDate: 'Scadenta',
        number: 'Serie si numar'
      },
      table: {
        reference: 'Factura',
        customer: 'Client',
        issuer: 'Emitent',
        amount: 'Total',
        dueDate: 'Scadenta {date}'
      },
      selection: {
        title: '{count} facturi selectate',
        description: 'Actiunile bulk raman rezervate; in milestone 05 pastram selectia pentru consistenta si revizuire.'
      },
      tabs: {
        general: 'General',
        lineItems: 'Linii',
        document: 'Document'
      },
      sections: {
        summary: 'Sumar factura',
        general: 'Date generale',
        parties: 'Parti',
        paymentTax: 'Plata si taxe',
        lineItems: 'Linii factura',
        document: 'Document',
        rightRail: 'Rezumat financiar'
      },
      fields: {
        series: 'Serie',
        number: 'Numar',
        issueDate: 'Data emiterii',
        dueDate: 'Scadenta',
        status: 'Status',
        customer: 'Client',
        project: 'Proiect',
        currency: 'Moneda',
        subtotal: 'Subtotal',
        taxTotal: 'TVA',
        grandTotal: 'Total',
        paidAt: 'Platita la',
        paidAmount: 'Suma incasata',
        paymentNotes: 'Note plata'
      },
      parties: {
        issuer: 'Emitent',
        customer: 'Client'
      },
      lineItems: {
        columns: {
          name: 'Serviciu',
          description: 'Descriere',
          quantity: 'Cantitate',
          unitPrice: 'Pret unitar',
          vat: 'TVA',
          lineTotal: 'Total linie'
        }
      },
      document: {
        pdfAsset: 'PDF atasat',
        syncState: 'Sincronizare',
        exportedAt: 'Exportata la',
        syncedAt: 'Sincronizata la',
        externalId: 'ID extern',
        invoice: 'Factura',
        proforma: 'Proforma'
      },
      actions: {
        syncOblio: 'Sincronizeaza Oblio'
      }
    },
    payments: {
      title: 'Plati',
      subtitle: '{active} plati active, {archived} arhivate',
      create: {
        title: 'Plata noua',
        snapshotNote: 'Aceste date sunt salvate doar pe linkul de plata si nu modifica profilul clientului.',
        termsNote: 'Completeaza termenii principali pentru acordul generat automat.',
        previewNote: 'Preview read-only: exact acest snapshot ajunge pe pagina publica de plata.',
        previewUntitled: 'Plata servicii web',
        previewService: 'Servicii web development/design Alttvision',
        sections: {
          clientIssuer: 'Client si prestator',
          projectTerms: 'Proiect, pret, plata si livrare',
          preview: 'Preview acord'
        }
      },
      list: {
        title: 'Comenzi de plata'
      },
      actions: {
        configure: 'Configureaza',
        create: 'Genereaza comanda',
        creating: 'Se genereaza...',
        save: 'Salveaza',
        sync: 'Sync public',
        refresh: 'Actualizeaza status',
        copyUrl: 'Copiaza URL',
        openUrl: 'Deschide URL',
        createContract: 'Creeaza contract pentru aceasta plata'
      },
      filters: {
        allCustomers: 'Toti clientii',
        allStatuses: 'Toate statusurile',
        allCurrencies: 'Toate monedele'
      },
      sort: {
        updatedAt: 'Actualizate recent',
        amount: 'Suma',
        status: 'Status',
        asc: 'Ascendent',
        desc: 'Descendent'
      },
      table: {
        title: 'Comanda',
        customer: 'Client',
        status: 'Status',
        amount: 'Suma',
        sync: 'Sync',
        updated: 'Actualizat',
        actions: 'Actiuni'
      },
      empty: {
        title: 'Nu exista plati',
        description: 'Creeaza prima comanda pentru a genera linkul public de acceptare si plata.',
        noContractsForCustomer: 'Nu exista contracte pentru clientul selectat.'
      },
      errors: {
        invalidAmount: 'Introdu o suma valida mai mare decat zero.',
        linkNotSynced: 'Linkul public nu este sincronizat inca. Ruleaza Sync public inainte de a-l trimite clientului.'
      },
      sync: {
        localOnlyWarning: 'Link local nesincronizat'
      },
      fields: {
        customer: 'Client',
        issuer: 'Emitent',
        agreementDate: 'Data acord',
        issuerRepresentative: 'Reprezentant prestator',
        customerLegalName: 'Nume legal client',
        customerIdentifier: 'CUI / CNP client',
        customerAddress: 'Adresa client',
        customerRepresentative: 'Reprezentant client',
        title: 'Titlu',
        serviceSummary: 'Sumar servicii',
        projectDescription: 'Descriere proiect in contract',
        paymentTerms: 'Termeni plata',
        deliveryWorkingDays: 'Livrare (zile lucratoare)',
        revisionRounds: 'Runde revizii',
        acceptanceWorkingDays: 'Acceptare (zile lucratoare)',
        warrantyDays: 'Garantie (zile)',
        amount: 'Suma',
        currency: 'Moneda',
        installmentLabel: 'Eticheta transa',
        contractTotal: 'Total contract',
        finalPaymentRemaining: 'Plata finala ramasa',
        invoiceStatus: 'Status factura',
        project: 'Proiect',
        contract: 'Contract',
        invoice: 'Factura',
        expiresAt: 'Expira la',
        status: 'Status',
        syncStatus: 'Status sync',
        acceptedAt: 'Acceptat la',
        paidAt: 'Platit la',
        publicToken: 'Token public',
        checkoutSession: 'Stripe Checkout',
        paymentIntent: 'Payment Intent',
        contractHash: 'Hash contract',
        checkboxText: 'Text bifare'
      },
      panels: {
        summary: 'Sumar plata',
        links: 'Link si Stripe'
      },
      tabs: {
        general: 'General',
        contract: 'Contract',
        evidence: 'Dovada'
      },
      sections: {
        details: 'Detalii comanda',
        contract: 'Contract si snapshot',
        evidence: 'Acceptari',
        events: 'Evenimente'
      },
      evidence: {
        emptyTitle: 'Fara acceptare inca',
        emptyDescription: 'Dovada legala va aparea dupa ce clientul bifeaza explicit si continua catre Stripe.'
      }
    },
    avi: {
      title: 'AVI',
      stub: {
        greeting: 'AVI nu este conectat inca. Poti scrie mesaje, iar noi pastram conversatia local.',
        placeholder: 'Scrie un mesaj...',
        reply: 'AVI nu este conectat inca. Mesajul tau a fost salvat local.',
        replyWithContext: 'AVI nu este conectat inca. Context: {context}'
      }
    },
    settings: {
      title: 'Setari',
      subtitle: 'Configuratii globale pentru emitere, contracte, integrare si experienta platformei.',
      tabs: {
        general: 'General',
        companyProfiles: 'Profiluri companie',
        customerTypes: 'Tip clienti',
        invoicing: 'Facturare',
        contracts: 'Contracte',
        payments: 'Plati',
        localEnvironment: 'Local',
        integrations: 'Integrari',
        avi: 'AVI si modele',
        localization: 'Localizare si aspect'
      },
      groups: {
        general: 'General',
        companyProfiles: 'Profiluri companie',
        customerTypes: 'Tip clienti',
        invoicing: 'Facturare',
        contracts: 'Contracte',
        payments: 'Plati si Stripe',
        localEnvironment: 'Mediu local',
        integrations: 'Integrari',
        avi: 'AVI si modele',
        localization: 'Localizare si aspect',
        wordpressPackages: 'Pachete WordPress'
      },
      fields: {
        agencyWorkspaceName: 'Nume workspace',
        defaultCurrency: 'Moneda implicita',
        defaultLanguage: 'Limba implicita',
        profileName: 'Nume profil',
        profileLegalName: 'Denumire legala',
        customerCategoryName: 'Nume tip',
        customerCategorySlug: 'Slug',
        customerCategoryIcon: 'Icon',
        customerCategorySortOrder: 'Ordine',
        cui: 'CIF / CUI',
        registrationNumber: 'Nr. Reg. Com.',
        addressLine: 'Adresa',
        city: 'Oras',
        county: 'Judet',
        country: 'Tara',
        email: 'Email',
        phone: 'Telefon',
        bankName: 'Banca',
        iban: 'IBAN',
        swift: 'SWIFT',
        invoiceSeries: 'Serie facturi',
        contractSignatureBlock: 'Bloc semnatura contract',
        vatPayer: 'Platitor TVA',
        isDefault: 'Profil implicit',
        defaultIssuer: 'Emitent implicit',
        defaultSeries: 'Serie implicita',
        numberingPrefix: 'Prefix numerotare',
        nextNumber: 'Urmatorul numar',
        paymentTermsDays: 'Termen plata (zile)',
        defaultVatRate: 'TVA implicit',
        pdfFooter: 'Footer PDF',
        defaultTemplate: 'Template implicit',
        signatureFooter: 'Footer semnare',
        xamppBasePath: 'XAMPP base path',
        defaultLocalDomainSuffix: 'Sufix domeniu local',
        mysqlHost: 'Host MySQL',
        mysqlPort: 'Port MySQL',
        mysqlUser: 'Utilizator MySQL',
        mysqlPassword: 'Parola MySQL',
        mysqlCharset: 'Charset MySQL',
        wordpressAdminUsername: 'User admin WordPress (implicit)',
        wordpressAdminPassword: 'Parola admin WordPress (implicita)',
        wordpressBaseThemePath: 'Cale tema alttvision-base',
        wordpressCorePluginPath: 'Cale plugin alttvision-core',
        oblioEnabled: 'Oblio activ',
        eFacturaEnabled: 'eFactura activa',
        oblioEmail: 'Email Oblio',
        oblioApiToken: 'Token API Oblio',
        oblioClientId: 'Email login Oblio',
        oblioApiSecret: 'Secret API Oblio',
        oblioIssuerCompanyName: 'Firma Oblio',
        oblioIssuerCif: 'CUI Oblio',
        oblioIssuerRegistrationNumber: 'Nr. Reg. Com. Oblio',
        oblioInvoiceSeries: 'Serie factura Oblio',
        oblioProformaSeries: 'Serie proforma Oblio',
        oblioDefaultVatName: 'Cota TVA Oblio',
        oblioDefaultVatPercentage: 'Procent TVA Oblio',
        eFacturaIssuerVat: 'CIF emitent eFactura',
        signingProvider: 'Provider semnare',
        signingApiKey: 'Cheie API semnare',
        paymentWebsiteBaseUrl: 'URL website public',
        paymentApiBaseUrl: 'URL API plati',
        paymentDefaultCurrency: 'Moneda implicita plati',
        paymentDefaultExpirationDays: 'Expirare implicita (zile)',
        stripeMode: 'Mod Stripe',
        stripePublishableKey: 'Stripe publishable key',
        stripeSecretKey: 'Stripe secret key',
        stripeWebhookSecret: 'Stripe webhook secret',
        websiteApiToken: 'Token API website',
        aviProvider: 'Provider AVI',
        aviModel: 'Model AVI',
        projectContextEnabled: 'Context proiect activ',
        aiDefaultProvider: 'Provider implicit',
        aiDefaultModel: 'Model implicit',
        aiTaskFullDesign: 'Model pentru design complet',
        aiTaskEditSection: 'Model pentru modificare sectiune',
        aiTaskCopyPolish: 'Model pentru rafinare text',
        aiCacheEnabled: 'Cache raspunsuri AI',
        aiAnthropicKey: 'Cheie API Anthropic',
        aiOpenAiKey: 'Cheie API OpenAI',
        locale: 'Limba interfetei',
        navigationDensity: 'Densitate navigare',
        reduceMotion: 'Reduce animatiile'
      },
      actions: {
        save: 'Salveaza',
        clearSecret: 'Sterge',
        newCompanyProfile: 'Profil nou',
        saveCompanyProfile: 'Salveaza profilul',
        newCustomerCategory: 'Tip nou',
        saveCustomerCategory: 'Salveaza tipul',
        archiveCustomerCategory: 'Arhiveaza',
        unarchiveCustomerCategory: 'Reactiveaza',
        browseFolder: 'Alege...',
        validateBridge: 'Valideaza',
        refreshBridgeContract: 'Reimprospateaza contract',
        testOblio: 'Testeaza Oblio',
        refreshOblio: 'Actualizeaza serii',
        saveAiKey: 'Salveaza cheia',
        clearAiKey: 'Sterge cheia',
        testAiKey: 'Testeaza cheia',
        clearAiCache: 'Curata cache-ul AI'
      },
      messages: {
        workspaceSaved: 'Setarile workspace-ului au fost salvate.',
        localizationSaved: 'Setarile de localizare au fost salvate.',
        companyProfileSaved: 'Profilul companiei a fost salvat.',
        customerCategorySaved: 'Tipul de client a fost salvat.',
        bridgeValidated: 'Bridge-ul WordPress a fost validat.',
        bridgeContractRefreshed: 'Contractul WordPress a fost reimprospatat.',
        bridgeUnavailable: 'Bridge-ul WordPress nu este disponibil.',
        paymentsSaved: 'Setarile pentru plati au fost salvate.',
        paymentSecretCleared: 'Secretul pentru plati a fost sters.',
        aiKeySaved: 'Cheia API a fost salvata securizat.',
        aiKeyCleared: 'Cheia API a fost stearsa.',
        aiKeyTestOk: 'Cheia API functioneaza. Latenta: {latency} ms.',
        aiCacheCleared: '{count} intrari au fost sterse din cache.'
      },
      payments: {
        nestFlowNote: 'Linkurile live sunt sincronizate cu alttvision.com prin tokenul API website. Fara token, linkurile raman locale pana cand le sincronizezi.'
      },
      oblio: {
        connectionChecked: 'Conexiunea Oblio a fost verificata.',
        statusReady: 'Oblio configurat',
        statusPending: 'Oblio neverificat',
        statusError: 'Oblio necesita atentie',
        company: 'Firma',
        invoiceNext: 'Urmatoarea factura',
        proforma: 'Proforma',
        noProformaSeries: 'Nu exista serie Proforma in Oblio'
      },
      customerTypes: {
        title: 'Tip clienti',
        preview: 'Preview',
        searchIcon: 'Cauta icon',
        untitled: 'Tip client'
      },
      options: {
        signingProvider: {
          none: 'Fara provider',
          reserved: 'Rezervat'
        },
        provider: {
          reserved: 'Rezervat'
        },
        aiTaskModel: {
          useDefault: 'Foloseste modelul implicit'
        },
        stripeMode: {
          test: 'Test',
          live: 'Live'
        },
        navigationDensity: {
          compact: 'Compacta',
          comfortable: 'Confortabila'
        }
      },
      wordpressPackages: {
        description: 'Bridge-ul foloseste manifestele temei si pluginului ca sursa de adevar pentru builder.',
        themeLabel: 'Tema (alttvision-base)',
        pluginLabel: 'Plugin (alttvision-core)',
        resolvedPath: 'Cale activa',
        bundledFallback: 'Pachet bundled',
        configuredPath: 'Cale configurata',
        packageVersion: 'Versiune {version}',
        packageNameUnknown: 'Manifest indisponibil',
        bridgeStatusValid: 'Bridge valid',
        bridgeStatusInvalid: 'Bridge invalid',
        bridgeStatusUnknown: 'Status necunoscut',
        contractHash: 'Hash contract: {hash}',
        contractHashUnavailable: 'Hash contract indisponibil',
        validatedAt: 'Validat la {timestamp}',
        noIssues: 'Nicio problema raportata.',
        issuesTitle: 'Probleme detectate',
        pickThemeDialogTitle: 'Selecteaza directorul temei alttvision-base',
        pickPluginDialogTitle: 'Selecteaza directorul pluginului alttvision-core',
        bundledThemeFolder: 'Tema bundled',
        bundledPluginFolder: 'Plugin bundled',
        loading: 'Se incarca statusul bridge-ului...'
      }
    },
    tasks: {
      title: 'Task-uri',
      filters: {
        all: 'Toate',
        allLabels: 'Toate etichetele',
        allProjects: 'Toate proiectele'
      },
      search: {
        placeholder: 'Cauta task...'
      },
      sort: {
        recent: 'Cele mai recente'
      },
      view: {
        kanban: 'Kanban',
        calendar: 'Calendar'
      },
      confirmDelete: 'Stergi acest task?',
      empty: {
        column: 'Nu exista task-uri in aceasta coloana.'
      },
      fields: {
        status: 'Status'
      },
      card: {
        actions: {
          focus: 'Focus',
          edit: 'Editeaza',
          delete: 'Sterge'
        }
      },
      modal: {
        createTitle: 'Adauga task',
        editTitle: 'Editeaza task',
        edit: 'Editeaza',
        dueAt: 'Termen',
        labels: 'Etichete',
        description: 'Descriere',
        noDescription: 'Fara descriere'
      },
      calendar: {
        title: 'Calendar',
        emptyTitle: 'Calendarul task-urilor ramane rezervat in acest milestone.',
        emptyDescription: 'Pastram aceleasi entitati si grupari dupa deadline fara sa implementam inca un calendar complet.',
        noDeadline: 'Fara deadline'
      }
    },
    resources: {
      summary: {
        subtitle: 'Sunt {total} de fisiere in {categories} categorii'
      },
      search: {
        placeholder: 'Cauta fisier...'
      },
      projectModal: {
        title: 'Resurse / Media',
        description: 'Biblioteca media pentru proiectul {projectName}. Fisierele incarcate aici pot fi folosite in Builder si AVI.',
        projectScope: 'Doar acest proiect',
        loading: 'Se incarca resursele proiectului...',
        searchPlaceholder: 'Cauta resurse in proiect...',
        sortLabel: 'Sortare',
        resultsCount: '{count} resurse vizibile',
        emptyTitle: 'Nu exista resurse in acest proiect',
        emptyDescription: 'Incarca imagini, iconuri sau documente pentru a le folosi rapid in Builder si AVI.',
        inspectorTitle: 'Detalii resursa',
        inspectorEmptyTitle: 'Selecteaza o resursa',
        inspectorEmptyDescription: 'Alege un element din lista pentru preview si editare.',
        confirmDeleteTitle: 'Stergi aceasta resursa?',
        confirmDeleteMessage: 'Resursa va fi mutata in Cos si nu va mai fi disponibila pentru acest proiect.',
        actions: {
          remove: 'Sterge resursa',
          confirmRemove: 'Confirma stergerea',
          removing: 'Se sterge...',
          saving: 'Se salveaza...'
        },
        messages: {
          loadError: 'Resursele proiectului nu pot fi incarcate.',
          uploadError: 'Incarcarea resurselor a esuat.',
          saveError: 'Salvarea resursei a esuat.',
          deleteError: 'Stergerea resursei a esuat.'
        },
        notifications: {
          uploadSuccessTitle: 'Resurse incarcate',
          uploadSuccessMessage: '{count} fisiere au fost adaugate proiectului.',
          uploadErrorTitle: 'Incarcarea a esuat',
          saveSuccessTitle: 'Resursa actualizata',
          saveSuccessMessage: 'Detaliile resursei au fost salvate local.',
          saveErrorTitle: 'Actualizarea resursei a esuat',
          deleteSuccessTitle: 'Resursa stearsa',
          deleteSuccessMessage: 'Resursa a fost mutata in Cos.',
          deleteErrorTitle: 'Stergerea resursei a esuat'
        }
      },
      actions: {
        upload: 'Incarca'
      },
      upload: {
        dropTitle: 'Trage fisiere aici',
        dropSubtitle: 'Elibereaza pentru a le incarca in Resurse',
        importing: 'Se incarca...'
      },
      modal: {
        title: 'Detalii resursa',
        notFound: 'Resursa nu a fost gasita.',
        loadFailed: 'Resursa indisponibila.',
        saveFailed: 'Salvarea a esuat.',
        validationName: 'Numele este obligatoriu.',
        updatedAt: 'Actualizat',
        fields: {
          altText: 'Alt text',
          description: 'Descriere'
        }
      },
      filters: {
        allProjects: 'Toate proiectele'
      },
      categories: {
        all: 'Toate',
        documents: 'Documente',
        images: 'Imagini',
        icons: 'Icons'
      },
      sort: {
        recent: 'Cele mai recente',
        oldest: 'Cele mai vechi',
        nameAsc: 'Nume A-Z',
        nameDesc: 'Nume Z-A'
      },
      table: {
        name: 'Resursa',
        kind: 'Tip',
        project: 'Proiect',
        customer: 'Client',
        updatedAt: 'Actualizat',
        status: 'Status'
      },
      selection: {
        title: '{count} resurse selectate',
        statusLabel: 'Status'
      },
      status: {
        active: 'Activ',
        archived: 'Arhivat'
      }
    },
    icons: {
      business: {
        badgeCheck: 'Validare',
        briefcaseBusiness: 'Servicii business',
        briefcaseMedical: 'Servicii medicale',
        building2: 'Companie',
        car: 'Auto',
        graduationCap: 'Educatie',
        hammer: 'Constructii',
        heartPulse: 'Sanatate',
        hospital: 'Clinica',
        house: 'Imobiliare',
        landmark: 'Institutii',
        monitorCog: 'Tehnologie',
        paintbrush: 'Design',
        pill: 'Farmacie',
        scale: 'Juridic',
        scissors: 'Beauty',
        shoppingBag: 'E-commerce',
        sparkles: 'Premium',
        sprayCan: 'Curatenie',
        store: 'Retail',
        stethoscope: 'Medic',
        truck: 'Transport',
        users: 'Comunitate',
        utensils: 'HoReCa',
        wrench: 'Service'
      }
    },
    enums: {
      projectStatus: {
        active: 'Activ',
        in_progress: 'In lucru',
        pending: 'In asteptare',
        complete: 'Finalizat',
        archived: 'Arhivat'
      },
      projectStage: {
        brief: 'Brief',
        branding: 'Branding',
        build: 'Build',
        review: 'Review',
        launch: 'Lansare',
        support: 'Suport'
      },
      deploymentState: {
        idle: 'Idle',
        ready: 'Ready',
        updating: 'Updating',
        warning: 'Atentie'
      },
      leadStatus: {
        new: 'Nou',
        to_call: 'De sunat',
        contacted: 'Contactat',
        follow_up: 'Follow-up',
        qualified: 'Calificat',
        converted: 'Convertit',
        lost: 'Pierdut'
      },
      leadActivityKind: {
        call: 'Apel',
        email: 'Email',
        whatsapp: 'WhatsApp',
        meeting: 'Intalnire',
        note: 'Nota',
        status_change: 'Schimbare status'
      },
      leadActivityOutcome: {
        reached: 'A raspuns',
        no_answer: 'Nu a raspuns',
        left_message: 'Mesaj lasat',
        interested: 'Interesat',
        not_interested: 'Neinteresat',
        scheduled_follow_up: 'Follow-up programat',
        qualified: 'Calificat',
        converted: 'Convertit',
        lost: 'Pierdut',
        note: 'Nota'
      },
      customerType: {
        company: 'Firma',
        sole_trader: 'PFA',
        individual: 'Persoana fizica'
      },
      customerStatus: {
        active: 'Client activ',
        inactive: 'Inactiv',
        lead: 'Lead',
        archived: 'Arhivat'
      },
      customerContractStatus: {
        draft: 'Draft',
        active: 'Activ',
        signed: 'Semnat',
        archived: 'Arhivat'
      },
      customerInvoiceStatus: {
        draft: 'Draft',
        issued: 'Emisa',
        paid: 'Platita',
        overdue: 'Intarziata',
        archived: 'Arhivata'
      },
      contractStatus: {
        draft: 'Draft',
        pending_review: 'In review',
        ready_to_send: 'Pregatit',
        sent: 'Trimis',
        signed: 'Semnat',
        expired: 'Expirat',
        archived: 'Arhivat'
      },
      contractTemplateStatus: {
        draft: 'Draft',
        active: 'Activ',
        archived: 'Arhivat'
      },
      contractTemplateCategory: {
        service_agreement: 'Acord servicii',
        website_development: 'Dezvoltare website',
        maintenance: 'Mentenanta',
        marketing: 'Marketing',
        hosting: 'Hosting',
        consulting: 'Consultanta',
        nda: 'NDA',
        annex: 'Anexa'
      },
      contractSignatureStatus: {
        not_started: 'Nepornit',
        pending: 'In asteptare',
        signed: 'Semnat',
        not_required: 'Nu este necesar'
      },
      invoiceStatus: {
        draft: 'Draft',
        issued: 'Emisa',
        sent: 'Trimisa',
        paid: 'Platita',
        partially_paid: 'Partial platita',
        overdue: 'Intarziata',
        archived: 'Arhivata'
      },
      invoiceSyncStatus: {
        not_configured: 'Neconfigurat',
        pending: 'In asteptare',
        synced: 'Sincronizat',
        error: 'Eroare'
      },
      paymentStatus: {
        draft: 'Draft',
        ready: 'Pregatita',
        sent: 'Trimisa',
        viewed: 'Vazuta',
        accepted: 'Acceptata',
        checkout_started: 'Checkout pornit',
        paid: 'Platita',
        payment_failed: 'Plata esuata',
        cancelled: 'Anulata',
        expired: 'Expirata',
        archived: 'Arhivata'
      },
      paymentSyncStatus: {
        not_configured: 'Neconfigurat',
        local_only: 'Doar local',
        pending: 'In asteptare',
        synced: 'Sincronizat',
        error: 'Eroare'
      },
      taskStatus: {
        backlog: 'Backlog',
        todo: 'To do',
        in_progress: 'In progres',
        done: 'Finalizat',
        archived: 'Arhivat'
      },
      taskPriority: {
        low: 'Scazuta',
        medium: 'Medie',
        high: 'Ridicata'
      },
      assetKind: {
        logo: 'Logo',
        icon: 'Iconita',
        document: 'Document',
        image: 'Imagine',
        pdf: 'PDF'
      },
      builderPageStatus: {
        draft: 'Draft',
        ready: 'Ready',
        archived: 'Archived'
      }
    },
    placeholder: {
      title: '{label} va fi definit dupa foundation.',
      description: 'Milestone 00 rezerva ruta, navigatia si cadrul vizual fara sa inventeze fluxul complet pentru acest domeniu.'
    }
  },
  'en-US': {
    navigation: {
      dashboard: 'Dashboard',
      projects: 'Projects',
      leads: 'Leads',
      tasks: 'Tasks',
      customers: 'Customers',
      resources: 'Resources',
      contracts: 'Contracts',
      invoices: 'Invoices',
      payments: 'Payments',
      avi: 'AVI',
      settings: 'Settings',
      help: 'Help'
    },
    shell: {
      searchPlaceholder: 'Search projects, leads, customers, contracts, invoices, payments...',
      searchShortcut: 'CTRL + K',
      profile: 'Profile',
      settings: 'Settings',
      signOut: 'Sign out',
      notification: 'Notifications',
      create: 'Create',
      delete: 'Delete',
      trash: 'Trash',
      sync: 'Sync',
      content: 'Content'
    },
    trash: {
      title: 'Trash',
      loading: 'Loading deleted items...',
      emptyTitle: 'Trash is empty',
      emptyDescription: 'No deleted items found.',
      confirmDeleteForever: 'Delete this item forever?',
      confirmClearTrash: 'Delete all {count} items from trash forever?',
      confirmClearTab: 'Delete the {count} items in this tab forever?',
      actions: {
        restore: 'Restore',
        deleteForever: 'Delete forever',
        clearTrash: 'Clear trash',
        clearTab: 'Clear tab'
      },
      filters: {
        all: 'All ({count})',
        projects: 'Projects ({count})',
        leads: 'Leads ({count})',
        tasks: 'Tasks ({count})',
        customers: 'Customers ({count})',
        contracts: 'Contracts ({count})',
        invoices: 'Invoices ({count})',
        payments: 'Payments ({count})'
      }
    },
    undo: {
      actions: {
        undo: 'Undo'
      },
      toast: {
        availableTitle: 'Action saved',
        availableMessage: 'You can quickly undo the last delete or edit.',
        undoneTitle: 'Action undone',
        undoneMessage: 'The last change was restored.',
        unavailableTitle: 'Cannot undo',
        unavailableMessage: 'There is no recent action to undo.',
        failedTitle: 'Undo failed',
        failedMessage: 'The action could not be undone.'
      }
    },
    search: {
      title: 'Search',
      inputPlaceholder: 'Search projects, leads, customers, contracts, invoices, and payments...',
      loading: 'Searching...',
      emptyTitle: 'No results yet',
      emptyDescription: 'Type a project, lead, customer, contract, invoice, or payment to search the workspace.'
    },
    quickAdd: {
      title: 'Add: {domain}',
      inlineTitle: 'Quick add: {domain}',
      domain: 'Section',
      shortcutHint: 'Ctrl/Cmd + Enter to submit',
      actions: {
        expandInline: 'Show quick add form',
        collapseInline: 'Hide quick add form'
      },
      fields: {
        name: 'Name',
        title: 'Title',
        description: 'Description',
        customer: 'Customer',
        project: 'Project',
        category: 'Category',
        legalType: 'Legal form',
        dueAt: 'Due date',
        priority: 'Priority',
        mimeType: 'MIME type',
        storagePath: 'Storage path',
        phone: 'Phone',
        currentWebsite: 'Current website',
        source: 'Source',
        tags: 'Tags'
      },
      validation: {
        nameRequired: 'Name is required.',
        titleRequired: 'Title is required.',
        customerRequired: 'Customer is required.'
      }
    },
    common: {
      seeAll: 'See all',
      more: 'More',
      notSet: 'Not configured',
      saved: 'Saved',
      invalid: 'Invalid',
      noDeadline: 'No deadline set',
      noResults: 'No results match the active filters.',
      openDetails: 'Open details',
      cancel: 'Cancel',
      send: 'Send',
      add: 'Add',
      remove: 'Remove',
      yes: 'Yes',
      no: 'No',
      close: 'Close',
      clear: 'Clear',
      archived: 'Archived',
      clearSelection: 'Clear selection',
      applyChanges: 'Apply changes',
      leaveUnchanged: 'Leave unchanged',
      setValue: 'Set',
      clearValue: 'Clear',
      confirmDeleteSoft: 'Delete this item? You can restore it from Trash.',
      columns: {
        settingsTitle: 'Visible columns',
        settingsDescription: 'Choose the columns visible in this section.',
        settingsReset: 'Reset',
        settingsLocked: 'Locked',
        settingsButton: 'Column settings'
      },
      view: {
        list: 'List',
        grid: 'Grid'
      },
      languages: {
        'ro-RO': 'Romanian',
        'en-US': 'English'
      }
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Cross-domain overview of operations and active delivery.',
      kpis: {
        activeProjects: 'Active projects',
        activeCustomers: 'Active customers',
        dueInvoices: 'Due invoices',
        activeContracts: 'Contracts'
      },
      panels: {
        recentActivity: 'Recent activity',
        financialOverview: 'Financial overview',
        payments: 'Payments',
        activeTasks: 'Active tasks',
        upcomingDeadlines: 'Upcoming deadlines',
        taskOverview: 'Task overview'
      },
      activity: {
        project_created: '{actorName} created a new project - {subject}',
        invoice_issued: '{actorName} issued an invoice for {subject}',
        contract_approved: '{actorName} approved a contract with {subject}',
        customer_added: '{actorName} added a new customer: {subject}',
        contract_review: '{actorName} started a contract review for {subject}'
      },
      deadlineKinds: {
        contract: 'Contract',
        invoice: 'Invoice',
        project: 'Project'
      },
      taskStatus: {
        backlog: 'Backlog',
        active: 'Active',
        complete: 'Complete',
        archived: 'Archived',
        removed: 'Removed'
      },
      financial: {
        issuedThisMonth: 'Invoices issued this month',
        collected: 'Collected',
        outstanding: 'Outstanding'
      },
      payments: {
        pending: 'Expected through Stripe',
        pendingCount: '{count} pending',
        paidCount: '{count} paid'
      }
    },
    projects: {
      title: 'Projects',
      subtitle: 'Main surface for managing and delivering websites.',
      summary: {
        active: '{count} active projects',
        archived: '{count} archived'
      },
      filters: {
        all: 'All',
        active: 'Active',
        in_progress: 'In progress',
        pending: 'Pending',
        complete: 'Complete',
        archived: 'Archived',
        allCustomers: 'All customers'
      },
      sort: {
        recent: 'Most recent',
        deadline: 'Closest deadline',
        name: 'Name A-Z'
      },
      view: {
        grid: 'Grid',
        list: 'List'
      },
      table: {
        name: 'Project',
        customer: 'Customer',
        status: 'Status',
        progress: 'Progress',
        deadline: 'Deadline'
      },
      card: {
        progress: 'Progress',
        launched: 'Launched on {date}',
        updated: 'Updated {value}',
        deadline: 'Deadline: {date}'
      },
      avi: {
        title: 'AVI',
        promptTitle: 'Create a new project for GreenSteps Solutions SRL. Build it as a WordPress site.',
        response: 'Sure, tell me the domain and what other details I should add to the project.'
      },
      bulk: {
        title: '{count} selected projects',
        clearValue: 'Clear value',
        setValue: 'Set value',
        assigneePlaceholder: 'Assignee',
        tagsPlaceholder: 'tag 1, tag 2',
        fields: {
          status: 'Status',
          customer: 'Customer',
          deadline: 'Deadline',
          assignee: 'Assignee',
          tags: 'Tags'
        }
      },
      placeholder: {
        title: 'This area will be extended after milestone 00.'
      }
    },
    leads: {
      title: 'Leads',
      quickAdd: {
        title: 'Quick add lead',
        actions: {
          add: 'Add lead'
        },
        validation: {
          nameRequired: 'Enter the lead name.'
        }
      },
      summary: {
        active: '{count} active leads',
        due: '{count} due',
        converted: '{count} converted'
      },
      filters: {
        all: 'All',
        status: 'Lead status',
        allCategories: 'All types',
        unassignedCategory: 'No type',
        categoryPlaceholder: 'Customer type',
        searchCategory: 'Search customer type',
        allCities: 'All cities',
        missingCity: 'No city',
        searchCity: 'Search city',
        allFollowUps: 'All follow-ups',
        due: 'Due',
        scheduled: 'Scheduled',
        noFollowUp: 'No follow-up'
      },
      sort: {
        nextContact: 'Next contact',
        addedAt: 'Added',
        recent: 'Most recent',
        name: 'Name A-Z',
        contact: 'Contact',
        category: 'Customer type',
        status: 'Status',
        website: 'Website',
        city: 'City'
      },
      view: {
        planner: 'Planner'
      },
      planner: {
        title: 'Weekly plan',
        selectedDay: 'Selected day',
        smartFill: 'Automatic planning',
        weekStart: 'Week',
        previousWeek: 'Previous week',
        nextWeek: 'Next week',
        thisWeek: 'Current week',
        planned: '{count} planned',
        contacted: '{count} contacted',
        emptyDayTitle: 'No planned leads',
        emptyDayDescription: 'Choose filters or run automatic planning for this week.',
        noCity: 'No city',
        groupLabel: '{city} / {category}',
        selectedCount: '{count} selected',
        assignSelected: 'Plan selection',
        clearSelected: 'Clear date',
        fields: {
          city: 'City',
          customerType: 'Customer type',
          startDate: 'Start from',
          dayCount: 'Days',
          leadsPerDay: 'Leads / day',
          statuses: 'Included statuses'
        },
        actions: {
          smartFill: 'Generate plan',
          smartFilling: 'Planning...'
        },
        messages: {
          manualAssigned: '{count} leads planned for {date}.',
          manualCleared: 'Contact date cleared for {count} leads.',
          smartFilled: '{planned} leads planned across {days} days. Skipped: {skipped}.'
        },
        badges: {
          contactedToday: 'Contacted today'
        }
      },
      table: {
        name: 'Lead',
        contact: 'Contact',
        category: 'Customer type',
        addedAt: 'Added',
        status: 'Status',
        nextContact: 'Next contact',
        website: 'Website',
        city: 'City'
      },
      card: {
        addedAt: 'Added',
        dueNow: 'Now: {date}',
        lastActivity: 'Last activity',
        nextContact: 'Contact: {date}',
        noActivity: 'No contact logged'
      },
      statusButtons: {
        ariaLabel: 'Lead status',
        current: 'Current status: {status} for {lead}',
        openMenu: 'Change status for {lead}',
        menuLabel: 'Choose lead status',
        set: 'Set {status} for {lead}'
      },
      selection: {
        title: '{count} selected leads',
        description: 'Selected leads can be moved to Trash and restored later.'
      },
      importExport: {
        tabs: {
          columns: 'Columns',
          importExport: 'Import / Export'
        },
        import: {
          title: 'Import JSON leads',
          description: 'Paste JSON, drop a file here, or leave the field empty to import directly from the clipboard.',
          pasteLabel: 'Import JSON',
          placeholder: 'Paste JSON here, or click Import JSON after copying data to the clipboard.',
          dropTitle: 'Drop JSON file here',
          dropSubtitle: 'Click for a file or use the text field.',
          fileReady: '{file} ready to import'
        },
        export: {
          title: 'Export leads',
          description: 'Export every active lead into a reusable JSON file.',
          dialogTitle: 'Export leads JSON'
        },
        demo: {
          title: 'Example JSON format'
        },
        actions: {
          uploadJson: 'Choose JSON file',
          pasteJson: 'Paste JSON',
          copyDemo: 'Copy example',
          preview: 'Preview',
          previewing: 'Checking...',
          import: 'Import JSON',
          importing: 'Importing...',
          export: 'Save export',
          exporting: 'Exporting...',
          copyExport: 'Copy export',
          create: 'New',
          skip_existing: 'Existing',
          duplicate_in_file: 'Duplicate',
          invalid: 'Invalid'
        },
        summary: {
          new: 'new',
          existing: 'existing',
          duplicates: 'duplicates',
          invalid: 'invalid',
          warnings: 'warnings'
        },
        preview: {
          row: 'Row {row}',
          match: 'Match: {name}',
          warningCount: '{count} warnings',
          ready: 'Ready to import'
        },
        reasons: {
          invalid_json: 'Invalid JSON',
          invalid_format: 'Format must be an array or an object with leads.',
          invalid_schema_version: 'Invalid schema version',
          unsupported_schema_version: 'Unsupported schema version',
          invalid_row: 'Row is not a JSON object.',
          missing_name: 'Lead name is missing.',
          invalid_fields: 'A field has an invalid format.',
          invalid_customer_category: 'Invalid customer type.',
          customer_category_not_found: 'Customer type not found.',
          duplicate_in_file: 'Already exists in the file.',
          existing_lead: 'Existing lead.',
          existing_deleted_lead: 'Lead found in Trash.'
        },
        messages: {
          demoCopied: 'Example copied.',
          exportCopied: 'Export copied.',
          exportSaved: 'JSON file saved.',
          importComplete: '{count} leads imported.',
          imported: '{count} leads were added.'
        },
        errors: {
          emptyJson: 'Paste JSON, choose a file, or copy JSON to the clipboard.',
          previewFailed: 'Import preview failed.',
          importFailed: 'Import failed.',
          fileReadFailed: 'The JSON file could not be read.',
          exportFailed: 'Export failed.',
          copyFailed: 'Copy failed.',
          clipboardUnavailable: 'Clipboard is unavailable.'
        }
      },
      editor: {
        subtitleFallback: 'Cold-call lead',
        tabs: {
          general: 'General',
          contact: 'Contact',
          conversion: 'Conversion'
        },
        sections: {
          general: 'Lead details',
          logActivity: 'Log contact',
          activityTimeline: 'Contact history',
          conversion: 'Convert to customer and project'
        },
        panels: {
          nextAction: 'Next action',
          lastActivity: 'Last contact',
          contact: 'Contact details',
          conversion: 'Conversion'
        },
        fields: {
          name: 'Lead name',
          status: 'Status',
          customerCategory: 'Customer type',
          companyName: 'Company',
          contactName: 'Contact person',
          phone: 'Phone',
          email: 'Email',
          currentWebsiteUrl: 'Current website',
          city: 'City',
          source: 'Source',
          nextContactAt: 'Next contact',
          tags: 'Tags',
          lostReason: 'Lost reason',
          activityKind: 'Contact type',
          activityOutcome: 'Outcome',
          comment: 'Comment'
        },
        actions: {
          logContact: 'Log contact',
          saveActivity: 'Save contact',
          convert: 'Create customer and project',
          viewConversion: 'View conversion'
        },
        empty: {
          activities: 'No contacts or comments are logged for this lead.'
        }
      },
      conversion: {
        newCustomer: 'New customer',
        customer: 'Customer',
        customerName: 'Customer name',
        projectName: 'Project name',
        primaryDomain: 'Website domain'
      }
    },
    project: {
      tabs: {
        general: 'General',
        builder: 'Builder',
        tasks: 'Tasks'
      },
      sections: {
        branding: 'Project settings and branding',
        social: 'Social media shown on site',
        websiteControl: 'Website control',
        wordpressAdmin: 'WordPress credentials',
        summary: 'Project summary',
        runtime: 'Local runtime',
        runtimeActivity: 'Runtime activity',
        builder: 'Builder workspace',
        tasks: 'Project tasks'
      },
      fields: {
        projectName: 'Project name',
        customer: 'Customer',
        status: 'Project status',
        summary: 'Summary / description',
        logo: 'Website logo',
        icon: 'Favicon / icon',
        primaryDomain: 'Primary website',
        localDomain: 'Local',
        runtimeStatus: 'Runtime status',
        siteRootPath: 'Site root',
        documentRootPath: 'Document root',
        databaseName: 'Database',
        databaseTablePrefix: 'Table prefix',
        httpsEnabled: 'HTTPS',
        lastUpdatedAt: 'Last update',
        builderRevision: 'Builder revision',
        runtimeOperation: 'Runtime operation',
        runtimeStep: 'Current step',
        runtimeStartedAt: 'Started at',
        runtimeFinishedAt: 'Finished at',
        wordpressAdminUsername: 'WordPress admin user',
        wordpressAdminPassword: 'WordPress admin password'
      },
      actions: {
        builder: 'Builder',
        update: 'Update',
        installSite: 'Install site',
        updateSite: 'Update site',
        installingSite: 'Installing...',
        updatingSite: 'Updating...',
        switchType: 'Switch runtime',
        uploadLogo: 'Upload SVG logo',
        uploadIcon: 'Upload SVG icon',
        openResources: 'Resources / Media',
        openPrimaryDomain: 'Open website',
        openLocalDomain: 'Open local',
        openBuilder: 'Open builder',
        saveCredentials: 'Save',
        clearProjectPassword: 'Clear project password'
      },
      branding: {
        emptyAsset: 'No SVG saved'
      },
      summary: {
        status: 'Status',
        stage: 'Stage',
        deployment: 'Deployment',
        progress: 'Progress',
        runtimeProgress: 'Runtime progress',
        recentChanges: 'Recent changes',
        runtimeMessage: 'Runtime message',
        wordpressAdminDefaultUsername: 'Default user',
        wordpressAdminProjectPassword: 'Project password',
        wordpressAdminDefaultPassword: 'Default password'
      },
      runtime: {
        idle: 'The local site is not provisioned yet.',
        ready: 'The local site is ready.',
        warning: 'The local runtime needs attention.',
        updating: 'The local runtime is updating.',
        notProvisioned: 'No local runtime exists for this project yet.',
        enabled: 'Enabled',
        disabled: 'Disabled',
        noDatabase: 'This runtime does not require a database.',
        operationKinds: {
          provision: 'Provision',
          update: 'Update',
          verify: 'Verify'
        },
        steps: {
          prepareContext: 'Preparing the local runtime',
          resetSiteRoot: 'Resetting the site root',
          generateBuilderArtifacts: 'Generating builder artifacts',
          prepareWordPressPackage: 'Preparing the WordPress package',
          extractWordPressCore: 'Extracting WordPress core',
          syncWordPressCore: 'Copying WordPress core files',
          createDatabase: 'Creating the database',
          writeWordPressConfig: 'Writing WordPress configuration',
          cleanupWordPressDefaults: 'Cleaning default WordPress assets',
          installWordPressStarter: 'Installing the Alttvision starter',
          runWordPressInstaller: 'Running the WordPress installer',
          verifyWordPressRuntime: 'Verifying the WordPress runtime',
          prepareUpdate: 'Preparing the runtime update',
          updatePhpRuntime: 'Updating the PHP runtime',
          verifyHttps: 'Verifying HTTPS',
          writeApacheBindings: 'Writing Apache bindings',
          reloadApache: 'Restarting Apache',
          verifyHttpsProbe: 'Checking the local HTTPS endpoint',
          regenerateCertificate: 'Regenerating the SSL certificate'
        }
      },
      notifications: {
        saveSuccessTitle: 'Project saved',
        saveSuccessMessage: 'Project changes were saved locally.',
        saveErrorTitle: 'Project save failed',
        credentialsSavedTitle: 'Credentials saved',
        credentialsSavedMessage: 'WordPress credentials were saved locally.',
        credentialsSaveErrorTitle: 'Credentials save failed',
        installStartedTitle: 'Installation started',
        installStartedMessage: 'Local website provisioning has started.',
        installSuccessTitle: 'Site installed',
        installWarningTitle: 'Install completed with warnings',
        installErrorTitle: 'Site install failed',
        updateStartedTitle: 'Update started',
        updateStartedMessage: 'Local website update has started.',
        updateSuccessTitle: 'Site updated',
        updateWarningTitle: 'Update completed with warnings',
        updateErrorTitle: 'Site update failed',
        openLocalErrorTitle: 'Local website unavailable',
        brandingUploadSuccessTitle: 'Branding updated',
        brandingUploadSuccessMessageLogo: 'Project logo updated.',
        brandingUploadSuccessMessageIcon: 'Project icon updated.',
        brandingUploadErrorTitle: 'Branding upload failed',
        brandingUploadInvalidFile: 'Please select an SVG file.',
        taskSavedTitle: 'Task saved',
        taskCreatedMessage: 'The task was added to the project.',
        taskUpdatedMessage: 'The project task was updated.',
        taskDeletedTitle: 'Task deleted',
        taskDeletedMessage: 'The task was moved to Trash.',
        taskDeleteErrorTitle: 'Task delete failed'
      },
      timeline: {
        branding_updated: 'Branding updated',
        content_synced: 'Content synced',
        builder_synced: 'Builder synced',
        update_ready: 'Update ready'
      },
      builder: {
        empty: {
          title: 'Builder ready for the first page',
          description: 'Create the first page and build the site only from simple, stable sections that export cleanly.',
          createFirstPage: 'Create first page'
        },
        toast: {
          title: 'Builder',
          saveFailed: 'The builder page could not be saved.',
          createFailed: 'The new page could not be created.',
          deleteFailed: 'The page could not be deleted.',
          primaryFailed: 'The primary page could not be updated.'
        },
        tabs: {
          content: 'Content',
          pages: 'Pages',
          seo: 'SEO',
          theme: 'Theme'
        },
        page: {
          primarySuffix: 'homepage',
          primaryBadge: 'Primary page',
          newPage: 'New page',
          setPrimary: 'Set as homepage',
          delete: 'Delete page'
        },
        pages: {
          title: 'Website pages',
          count: '{count} pages',
          settingsTitle: 'Page settings',
          primaryStatus: 'Page role',
          secondaryPage: 'Secondary page',
          sectionCountLabel: 'Active sections'
        },
        status: {
          valid: 'Valid',
          errors: '{count} errors',
          warnings: '{count} warnings',
          saving: 'Saving...',
          unsaved: 'Unsaved changes',
          saved: 'Saved locally'
        },
        structure: {
          title: 'Page structure',
          sectionCount: '{count} sections',
          addSection: 'Add section',
          emptyTitle: 'No sections yet',
          emptyDescription: 'Add the first approved section to start building the page.'
        },
        preview: {
          title: 'Live preview',
          emptyTitle: 'Page unavailable',
          emptyDescription: 'Select or create a page to preview it.',
          devices: {
            desktop: 'Desktop',
            tablet: 'Tablet',
            mobile: 'Mobile'
          }
        },
        inspector: {
          pageTitle: 'Page settings',
          sectionTitle: 'Section settings',
          emptyTitle: 'Choose a section',
          emptyDescription: 'Select a section from the structure or preview to edit its content.'
        },
        seo: {
          title: 'SEO and metadata',
          previewTitle: 'Snippet preview',
          previewDescription: 'The title and description are ready for export and future AI workflows.',
          previewFallback: 'Add a short description to clarify the page intent and benefits.'
        },
        theme: {
          title: 'Style and theme preset',
          description: 'Choose a stable project-wide preset for layout, tone, and future generation flows.',
          currentTitle: 'Selected preset',
          placeholder: 'The preset is stored on the project. In this phase we define the selection and branding without rewriting content automatically.',
          noBrandingAsset: 'No asset selected',
          updateFailed: 'The preset could not be updated.'
        },
        themePresets: {
          real_estate: {
            label: 'Real Estate',
            description: 'Property showcases, lead forms, and listing-first presentation.'
          },
          dental_clinic: {
            label: 'Dental Clinic',
            description: 'Clinical clarity, service trust, and institutional presentation.'
          },
          beauty_clinic: {
            label: 'Beauty Clinic',
            description: 'Editorial treatment pages, premium packages, and polished conversion flows.'
          },
          restaurant: {
            label: 'Restaurant',
            description: 'Menu storytelling, atmosphere, and booking-focused presentation.'
          },
          corporate_services: {
            label: 'Corporate Services',
            description: 'The neutral preset for agencies, B2B firms, and professional presentation sites.'
          }
        },
        picker: {
          title: 'Section library',
          close: 'Close'
        },
        dialogs: {
          create: {
            title: 'New page',
            description: 'Set the initial structure for a simple page that can export to both PHP and WordPress.',
            submit: 'Create page',
            validation: {
              titleRequired: 'Enter a page title.'
            },
            defaults: {
              homepageTitle: 'Homepage',
              standardTitle: 'New page'
            }
          },
          delete: {
            title: 'Delete page',
            description: 'The page "{title}" will be removed from the builder and from the synced runtime output.',
            submit: 'Delete page'
          }
        },
        fields: {
          pageTitle: 'Page title',
          slug: 'Slug',
          pageType: 'Page type',
          status: 'Status',
          seoTitle: 'SEO title',
          seoDescription: 'SEO description',
          tone: 'Tone',
          eyebrow: 'Eyebrow',
          heading: 'Heading',
          subheading: 'Subheading',
          alignment: 'Alignment',
          primaryCta: 'Primary CTA',
          secondaryCta: 'Secondary CTA',
          media: 'Media',
          intro: 'Intro',
          body: 'Body',
          title: 'Title',
          question: 'Question',
          value: 'Value',
          quote: 'Quote',
          caption: 'Caption',
          label: 'Label',
          author: 'Author',
          role: 'Role',
          description: 'Description',
          answer: 'Answer',
          image: 'Image',
          icon: 'Icon',
          address: 'Address',
          phone: 'Phone',
          email: 'Email',
          mapLabel: 'Map label',
          companyName: 'Company name',
          tagline: 'Tagline',
          link: 'Link',
          text: 'Text',
          style: 'Style',
          altText: 'Alt text',
          menuLocation: 'Menu location'
        },
        menuLocations: {
          none: 'Do not show in menu',
          primary: 'Primary header',
          footer: 'Footer'
        },
        actions: {
          maximizeWorkspace: 'Maximize builder',
          restoreWorkspace: 'Restore builder',
          moveUp: 'Up',
          moveDown: 'Down',
          duplicate: 'Duplicate',
          hide: 'Hide',
          show: 'Show',
          enable: 'Enable',
          deleteSection: 'Delete section',
          addHighlight: 'Add highlight',
          deleteHighlight: 'Delete highlight',
          addItem: 'Add item',
          deleteItem: 'Delete item',
          addLink: 'Add link',
          deleteLink: 'Delete link'
        },
        alignments: {
          start: 'Start',
          center: 'Center',
          end: 'End'
        },
        tones: {
          default: 'Default',
          brand: 'Brand',
          muted: 'Muted',
          contrast: 'Contrast'
        },
        buttonStyles: {
          primary: 'Primary',
          secondary: 'Secondary'
        },
        assets: {
          none: 'No asset',
          selected: 'Selected asset',
          icon: 'Icon'
        },
        contact: {
          staticHint: 'Forms are not active in v1. This section exports only contact details and links.'
        },
        defaults: {
          highlight: 'New highlight'
        },
        validation: {
          error: 'Error',
          warning: 'Warning'
        },
        pageTypes: {
          homepage: {
            label: 'Homepage',
            description: 'Primary landing page with presentation sections and a clear CTA.'
          },
          standard: {
            label: 'Standard page',
            description: 'Simple content page for services, details, or presentation.'
          },
          contact: {
            label: 'Contact',
            description: 'Static contact page with practical details and CTA links.'
          }
        },
        variants: {
          split: 'Split',
          default: 'Default',
          cards: 'Cards',
          grid: 'Grid',
          numbered: 'Numbered',
          band: 'Band',
          accordion: 'Accordion',
          banner: 'Banner',
          'details-form': 'Details',
          simple: 'Simple'
        },
        sectionCatalog: {
          hero: {
            label: 'Hero',
            description: 'Primary headline, concise copy, and clear actions.'
          },
          about: {
            label: 'About',
            description: 'Context, intro, and key points about the brand or page.'
          },
          services: {
            label: 'Services',
            description: 'Compact service list shown as cards.'
          },
          features: {
            label: 'Features',
            description: 'Simple benefit or differentiator grid.'
          },
          steps: {
            label: 'Steps',
            description: 'Clear numbered process section.'
          },
          stats: {
            label: 'Stats',
            description: 'Short numeric highlights for quick scanning.'
          },
          testimonials: {
            label: 'Testimonials',
            description: 'Short quotes from clients or partners.'
          },
          faq: {
            label: 'FAQ',
            description: 'Frequent questions with concise answers.'
          },
          cta: {
            label: 'CTA',
            description: 'Conversion block with up to two actions.'
          },
          contact: {
            label: 'Contact',
            description: 'Static contact details without form backend.'
          },
          gallery: {
            label: 'Gallery',
            description: 'Simple image grid with optional captions.'
          },
          footer: {
            label: 'Footer',
            description: 'Page ending with links and contact details.'
          }
        },
        ai: {
          bubble: {
            open: 'AI assistant',
            close: 'Close assistant'
          },
          title: 'Builder AI assistant',
          subtitle: 'Generate and refine pages using the approved section library.',
          modes: {
            fullDesign: 'Full design',
            editSection: 'Edit section',
            copyPolish: 'Polish copy'
          },
          modeHints: {
            fullDesign: 'Regenerate the whole page from your brand and prompt.',
            editSection: 'Rewrite the selected section based on your instructions.',
            copyPolish: 'Refine the copy of the current section without changing structure.'
          },
          context: {
            activePage: 'Page: {title}',
            activeSection: 'Section: {label}',
            noSection: 'Select a section from the structure to edit it.',
            noPage: 'Select or create a page to use AI.'
          },
          prompt: {
            label: 'Prompt',
            placeholder: 'Describe in Romanian (preferred) how the page or section should look...',
            exampleHint: 'Tip: mention the business type, atmosphere, and desired CTAs.'
          },
          actions: {
            generate: 'Generate',
            generating: 'Generating...',
            apply: 'Apply changes',
            discard: 'Discard',
            undo: 'Undo change',
            retry: 'Retry',
            clearCache: 'Clear cache'
          },
          status: {
            idle: 'Ready',
            loading: 'AI is working on the result...',
            ready: 'Proposal ready to apply',
            applied: 'Changes applied.',
            undone: 'Change undone.',
            cached: 'Reused from cache'
          },
          diff: {
            title: 'AI proposal',
            sections: '{count} sections',
            newLabel: 'New',
            changedLabel: 'Changed'
          },
          errors: {
            noKey: 'Add an API key in Settings > AI to use this feature.',
            noSection: 'Select a section for this mode.',
            noPage: 'Select a page to use AI.',
            generic: 'Generation failed. Try again or change the prompt.',
            invalidJson: 'The AI response did not match the schema. Try a different prompt.'
          },
          model: {
            label: 'Model',
            provider: {
              anthropic: 'Anthropic',
              openai: 'OpenAI'
            }
          }
        }
      },
      tasks: {
        title: 'No tasks for this project yet',
        description: 'Add the first task to track work directly from the project.',
        actions: {
          add: 'Add task'
        }
      },
      social: {
        facebook: 'Facebook',
        instagram: 'Instagram',
        googleBusiness: 'Google Business',
        youtube: 'YouTube'
      }
    },
    customers: {
      title: 'Customers',
      summary: {
        active: '{count} active customers',
        archived: '{count} archived'
      },
      filters: {
        all: 'All',
        active: 'Active',
        inactive: 'Inactive',
        lead: 'Leads',
        archived: 'Archived',
        allTypes: 'All types',
        allCategories: 'All types',
        unassignedCategory: 'No type',
        categoryPlaceholder: 'Customer type',
        searchCategory: 'Search customer type'
      },
      sort: {
        recent: 'Most recent',
        name: 'Name A-Z'
      },
      table: {
        name: 'Name',
        type: 'Customer type',
        category: 'Customer type',
        contact: 'Contact person',
        phone: 'Phone number',
        noContact: 'No primary contact',
        noPhone: 'No phone number'
      },
      editor: {
        tabs: {
          general: 'General',
          projects: 'Projects',
          contracts: 'Contracts',
          invoices: 'Invoices'
        },
        hero: {
          favorite: 'Favorite'
        },
        site: {
          live: 'Live site',
          local: 'Local site'
        },
        sections: {
          general: 'Customer details',
          primaryContact: 'Primary contact',
          socialMedia: 'Social media',
          projects: 'Projects',
          contracts: 'Contracts',
          invoices: 'Invoices',
          contactAddress: 'Contact details and addresses',
          notes: 'Notes'
        },
        fields: {
          name: 'Customer name',
          status: 'Status',
          legalName: 'Legal name',
          cui: 'VAT / CUI',
          registrationNumber: 'Registration no.',
          vatPayer: 'VAT payer',
          email: 'Email',
          website: 'Website',
          customerType: 'Legal form',
          customerCategory: 'Customer type',
          legalType: 'Legal form',
          shortDescription: 'Short description'
        },
        summary: {
          activeSince: 'Customer active since {value}',
          activeSinceUnknown: 'Active customer',
          completedProjects: '{count} completed projects',
          generatedRevenue: 'Generated revenue'
        },
        actions: {
          addNote: 'Add note',
          addAddress: 'Add address'
        },
        projects: {
          columns: {
            project: 'Project',
            status: 'Status',
            type: 'Type',
            updated: 'Updated'
          },
          updated: 'Updated {date}'
        },
        contracts: {
          expires: 'Expires {date}',
          noEndDate: 'No end date'
        },
        invoices: {
          due: 'Due {date}'
        },
        empty: {
          contact: 'The primary contact will be defined in a later milestone.',
          socialMedia: 'The social media surface is reserved for a later milestone.',
          projects: 'No projects are linked to this customer.',
          contracts: 'No contracts are linked to this customer.',
          invoices: 'No invoices are linked to this customer.',
          notes: 'No notes are available for this customer.'
        }
      },
      selection: {
        title: '{count} selected customers',
        description: 'Bulk editing for customers stays reserved until domain mutation contracts are defined.'
      }
    },
    contracts: {
      title: 'Contracts',
      summary: {
        active: '{count} active contracts',
        archived: '{count} archived',
        effectiveFrom: 'From',
        effectiveUntil: 'Until',
        value: 'Value',
        signature: 'Signature',
        issuer: 'Issuer',
        customer: 'Customer',
        generated: 'Generated'
      },
      filters: {
        all: 'All',
        draft: 'Draft',
        pending_review: 'In review',
        ready_to_send: 'Ready to send',
        signed: 'Signed',
        archived: 'Archived',
        allCustomers: 'All customers',
        allProjects: 'All projects'
      },
      sort: {
        recent: 'Recently updated',
        effective: 'Ending soon',
        title: 'Title A-Z'
      },
      table: {
        title: 'Contract',
        customer: 'Customer',
        project: 'Project',
        value: 'Value',
        effectiveUntil: 'Valid until {date}',
        noEndDate: 'No end date'
      },
      selection: {
        title: '{count} selected contracts',
        description: 'Multi-select remains available for review only in milestone 05.'
      },
      actions: {
        openTemplates: 'Contract templates'
      },
      tabs: {
        general: 'General',
        document: 'Document',
        activity: 'Activity'
      },
      sections: {
        summary: 'Contract summary',
        general: 'General details',
        parties: 'Parties',
        terms: 'Terms',
        financials: 'Financials',
        document: 'Document',
        activity: 'Activity',
        documentSummary: 'Document summary'
      },
      fields: {
        title: 'Contract title',
        contractNumber: 'Contract number',
        status: 'Status',
        template: 'Template',
        customer: 'Customer',
        project: 'Project',
        effectiveFrom: 'Effective from',
        effectiveUntil: 'Effective until',
        scope: 'Service scope',
        renewalNotes: 'Renewal notes',
        terminationNotes: 'Termination notes',
        value: 'Value',
        currency: 'Currency',
        paymentNotes: 'Payment notes',
        scheduleSummary: 'Schedule summary'
      },
      parties: {
        issuer: 'Issuer',
        customer: 'Customer'
      },
      document: {
        templateVersion: 'Template version',
        generatedAt: 'Generated at',
        signatureStatus: 'Signature status',
        sourceFormat: 'Source format',
        pdfAsset: 'Attached PDF',
        resolvedBody: 'Resolved document'
      },
      empty: {
        activity: 'No activity is available for this contract.'
      },
      activityKinds: {
        created: 'Created',
        generated: 'Generated',
        sent: 'Sent',
        signed: 'Signed',
        updated: 'Updated'
      },
      templates: {
        title: 'Contract templates',
        subtitle: 'Versioned blueprints for Romanian-first contracts and controlled document generation.',
        newTitle: 'New template',
        summary: {
          active: '{count} active',
          draft: '{count} draft',
          archived: '{count} archived'
        },
        actions: {
          backToContracts: 'Back to contracts',
          new: 'New template',
          saveTemplate: 'Save template',
          addPlaceholder: 'Add placeholder',
          removePlaceholder: 'Remove',
          refreshPreview: 'Refresh preview',
          generateContract: 'Generate contract'
        },
        filters: {
          all: 'All',
          draft: 'Draft',
          active: 'Active',
          archived: 'Archived',
          allCategories: 'All categories',
          allLanguages: 'All languages'
        },
        sort: {
          updated: 'Recently updated',
          name: 'Name A-Z',
          version: 'Current version'
        },
        table: {
          template: 'Template',
          category: 'Category',
          issuer: 'Default issuer',
          version: 'Version'
        },
        selection: {
          title: '{count} selected templates',
          description: 'Selection remains available for collection consistency and quick review.'
        },
        tabs: {
          general: 'General',
          body: 'Body',
          placeholders: 'Placeholders',
          outputRules: 'Output rules',
          versions: 'Versions'
        },
        sections: {
          summary: 'Template summary',
          general: 'General details',
          body: 'Template body',
          placeholders: 'Placeholder schema',
          outputRules: 'Output rules',
          versions: 'Version history',
          generation: 'Contract generation',
          manualTokens: 'Manual values',
          preview: 'Document preview'
        },
        fields: {
          name: 'Template name',
          slug: 'Slug',
          category: 'Category',
          language: 'Language',
          status: 'Status',
          defaultIssuer: 'Default issuer',
          notes: 'Internal notes',
          bodySource: 'Markdown content',
          numberingPrefix: 'Numbering prefix',
          signatureFooter: 'Signature footer',
          changeSummary: 'Change summary',
          required: 'Required',
          customer: 'Customer',
          project: 'Project',
          issuer: 'Issuer',
          contractTitle: 'Contract title',
          effectiveFrom: 'Effective from',
          effectiveUntil: 'Effective until',
          totalValue: 'Total value',
          currency: 'Currency',
          scopeSummary: 'Service scope',
          paymentNotes: 'Payment notes',
          scheduleSummary: 'Delivery schedule',
          renewalNotes: 'Renewal notes',
          terminationNotes: 'Termination notes'
        },
        summaryFields: {
          slug: 'Slug',
          placeholders: 'Placeholders',
          manualTokens: 'Manual tokens',
          previewStatus: 'Preview status'
        },
        placeholderColumns: {
          key: 'Key',
          meta: 'Source and type',
          label: 'Label key',
          fallback: 'Fallback'
        },
        groups: {
          customer: 'Customer',
          issuer: 'Issuer',
          project: 'Project',
          contract: 'Contract',
          manual: 'Manual'
        },
        sources: {
          customer: 'Customer data',
          issuer: 'Issuer data',
          project: 'Project data',
          contract: 'Contract data',
          manual: 'Manual value'
        },
        valueTypes: {
          string: 'Text',
          multiline: 'Long text',
          currency: 'Currency',
          date: 'Date'
        },
        messages: {
          templateSaved: 'Template was saved.',
          saveToPreview: 'Save template changes before previewing or generating.'
        },
        empty: {
          placeholders: 'Add the first placeholder to build the template schema.',
          versions: 'No saved versions are available for this template yet.'
        },
        preview: {
          pending: 'Waiting for preview',
          ready: 'Preview updated',
          saveRequired: 'Save the template to enable preview and generation.',
          empty: 'Complete the context and refresh the preview to resolve the document.',
          contractNumber: 'Contract number',
          contractTitle: 'Contract title',
          currency: 'Currency',
          missingTokens: 'Missing tokens',
          customerSnapshot: 'Customer snapshot',
          issuerSnapshot: 'Issuer snapshot',
          body: 'Resolved body'
        },
        versions: {
          versionLabel: 'v{version}',
          current: 'Current',
          none: 'No version',
          noChangeSummary: 'No change summary'
        },
        placeholders: {
          issuerLegalName: 'Issuer legal name',
          issuerIban: 'Issuer IBAN',
          customerLegalName: 'Customer legal name',
          customerRegistrationNumber: 'Customer registration no.',
          customerBillingAddress: 'Customer billing address',
          customerCui: 'Customer CUI',
          customerSignatoryName: 'Customer representative',
          projectName: 'Project name',
          projectPrimaryDomain: 'Project primary domain',
          contractNumber: 'Contract number',
          contractIssueDate: 'Issue date',
          contractCurrency: 'Contract currency',
          contractTotalValue: 'Contract total value',
          contractScopeSummary: 'Service scope',
          contractEffectiveFrom: 'Effective from',
          contractPaymentNotes: 'Payment notes',
          contractScheduleSummary: 'Delivery schedule',
          contractTerminationNotes: 'Termination notes'
        }
      }
    },
    invoices: {
      title: 'Invoices',
      summary: {
        active: '{count} active invoices',
        archived: '{count} archived',
        issueDate: 'Issued',
        dueDate: 'Due',
        total: 'Total',
        sync: 'Sync',
        subtotal: 'Subtotal',
        tax: 'Tax'
      },
      filters: {
        all: 'All',
        draft: 'Draft',
        issued: 'Issued',
        paid: 'Paid',
        overdue: 'Overdue',
        archived: 'Archived',
        allCustomers: 'All customers',
        allIssuers: 'All issuers',
        allDueStates: 'All due states',
        open: 'Open',
        overdueState: 'Overdue',
        paidState: 'Paid'
      },
      sort: {
        issueDate: 'Issue date',
        dueDate: 'Due date',
        number: 'Series and number'
      },
      table: {
        reference: 'Invoice',
        customer: 'Customer',
        issuer: 'Issuer',
        amount: 'Amount',
        dueDate: 'Due {date}'
      },
      selection: {
        title: '{count} selected invoices',
        description: 'Bulk write actions stay reserved; milestone 05 keeps selection only for consistency and review.'
      },
      tabs: {
        general: 'General',
        lineItems: 'Line items',
        document: 'Document'
      },
      sections: {
        summary: 'Invoice summary',
        general: 'General details',
        parties: 'Parties',
        paymentTax: 'Payment and tax',
        lineItems: 'Line items',
        document: 'Document',
        rightRail: 'Financial summary'
      },
      fields: {
        series: 'Series',
        number: 'Number',
        issueDate: 'Issue date',
        dueDate: 'Due date',
        status: 'Status',
        customer: 'Customer',
        project: 'Project',
        currency: 'Currency',
        subtotal: 'Subtotal',
        taxTotal: 'Tax',
        grandTotal: 'Grand total',
        paidAt: 'Paid at',
        paidAmount: 'Collected amount',
        paymentNotes: 'Payment notes'
      },
      parties: {
        issuer: 'Issuer',
        customer: 'Customer'
      },
      lineItems: {
        columns: {
          name: 'Service',
          description: 'Description',
          quantity: 'Quantity',
          unitPrice: 'Unit price',
          vat: 'VAT',
          lineTotal: 'Line total'
        }
      },
      document: {
        pdfAsset: 'Attached PDF',
        syncState: 'Sync state',
        exportedAt: 'Exported at',
        syncedAt: 'Synced at',
        externalId: 'External ID',
        invoice: 'Invoice',
        proforma: 'Proforma'
      },
      actions: {
        syncOblio: 'Sync Oblio'
      }
    },
    payments: {
      title: 'Payments',
      subtitle: '{active} active payments, {archived} archived',
      create: {
        title: 'New payment',
        snapshotNote: 'These values are saved only on this payment link and do not update the customer profile.',
        termsNote: 'Fill the main commercial terms for the generated agreement.',
        previewNote: 'Read-only preview: this exact snapshot appears on the public payment page.',
        previewUntitled: 'Web services payment',
        previewService: 'Alttvision web development/design services',
        sections: {
          clientIssuer: 'Client and provider',
          projectTerms: 'Project, price, payment and delivery',
          preview: 'Agreement preview'
        }
      },
      list: {
        title: 'Payment orders'
      },
      actions: {
        configure: 'Configure',
        create: 'Generate order',
        creating: 'Generating...',
        save: 'Save',
        sync: 'Sync public',
        refresh: 'Refresh status',
        copyUrl: 'Copy URL',
        openUrl: 'Open URL',
        createContract: 'Create contract for this payment'
      },
      filters: {
        allCustomers: 'All customers',
        allStatuses: 'All statuses',
        allCurrencies: 'All currencies'
      },
      sort: {
        updatedAt: 'Recently updated',
        amount: 'Amount',
        status: 'Status',
        asc: 'Ascending',
        desc: 'Descending'
      },
      table: {
        title: 'Order',
        customer: 'Customer',
        status: 'Status',
        amount: 'Amount',
        sync: 'Sync',
        updated: 'Updated',
        actions: 'Actions'
      },
      empty: {
        title: 'No payments yet',
        description: 'Create the first order to generate a public acceptance and payment link.',
        noContractsForCustomer: 'No contracts exist for the selected customer.'
      },
      errors: {
        invalidAmount: 'Enter a valid amount greater than zero.',
        linkNotSynced: 'The public link is not synced yet. Run Sync public before sending it to the customer.'
      },
      sync: {
        localOnlyWarning: 'Unsynced local link'
      },
      fields: {
        customer: 'Customer',
        issuer: 'Issuer',
        agreementDate: 'Agreement date',
        issuerRepresentative: 'Provider representative',
        customerLegalName: 'Client legal name',
        customerIdentifier: 'Client CUI / CNP',
        customerAddress: 'Client address',
        customerRepresentative: 'Client representative',
        title: 'Title',
        serviceSummary: 'Service summary',
        projectDescription: 'Project description in contract',
        paymentTerms: 'Payment terms',
        deliveryWorkingDays: 'Delivery (working days)',
        revisionRounds: 'Revision rounds',
        acceptanceWorkingDays: 'Acceptance (working days)',
        warrantyDays: 'Warranty (days)',
        amount: 'Amount',
        currency: 'Currency',
        installmentLabel: 'Installment label',
        contractTotal: 'Contract total',
        finalPaymentRemaining: 'Final payment remaining',
        invoiceStatus: 'Invoice status',
        project: 'Project',
        contract: 'Contract',
        invoice: 'Invoice',
        expiresAt: 'Expires at',
        status: 'Status',
        syncStatus: 'Sync status',
        acceptedAt: 'Accepted at',
        paidAt: 'Paid at',
        publicToken: 'Public token',
        checkoutSession: 'Stripe Checkout',
        paymentIntent: 'Payment Intent',
        contractHash: 'Contract hash',
        checkboxText: 'Checkbox text'
      },
      panels: {
        summary: 'Payment summary',
        links: 'Link and Stripe'
      },
      tabs: {
        general: 'General',
        contract: 'Contract',
        evidence: 'Evidence'
      },
      sections: {
        details: 'Order details',
        contract: 'Contract and snapshot',
        evidence: 'Acceptances',
        events: 'Events'
      },
      evidence: {
        emptyTitle: 'No acceptance yet',
        emptyDescription: 'Legal evidence appears after the customer explicitly checks the box and continues to Stripe.'
      }
    },
    avi: {
      title: 'AVI',
      stub: {
        greeting: "AVI isn't connected yet. You can still write messages and keep this conversation locally.",
        placeholder: 'Write a message...',
        reply: "AVI isn't connected yet. Your message was saved locally.",
        replyWithContext: "AVI isn't connected yet. Context: {context}"
      }
    },
    settings: {
      title: 'Settings',
      subtitle: 'Global configuration for issuing, contracts, integrations, and platform experience.',
      tabs: {
        general: 'General',
        companyProfiles: 'Company profiles',
        customerTypes: 'Customer types',
        invoicing: 'Invoicing',
        contracts: 'Contracts',
        payments: 'Payments',
        localEnvironment: 'Local environment',
        integrations: 'Integrations',
        avi: 'AVI & models',
        localization: 'Localization & appearance'
      },
      groups: {
        general: 'General',
        companyProfiles: 'Company profiles',
        customerTypes: 'Customer types',
        invoicing: 'Invoicing',
        contracts: 'Contracts',
        payments: 'Payments and Stripe',
        localEnvironment: 'Local environment',
        integrations: 'Integrations',
        avi: 'AVI & models',
        localization: 'Localization & appearance',
        wordpressPackages: 'WordPress packages'
      },
      fields: {
        agencyWorkspaceName: 'Workspace name',
        defaultCurrency: 'Default currency',
        defaultLanguage: 'Default language',
        profileName: 'Profile name',
        profileLegalName: 'Legal name',
        customerCategoryName: 'Type name',
        customerCategorySlug: 'Slug',
        customerCategoryIcon: 'Icon',
        customerCategorySortOrder: 'Order',
        cui: 'VAT / CUI',
        registrationNumber: 'Registration no.',
        addressLine: 'Address line',
        city: 'City',
        county: 'County',
        country: 'Country',
        email: 'Email',
        phone: 'Phone',
        bankName: 'Bank name',
        iban: 'IBAN',
        swift: 'SWIFT',
        invoiceSeries: 'Invoice series',
        contractSignatureBlock: 'Contract signature block',
        vatPayer: 'VAT payer',
        isDefault: 'Default profile',
        defaultIssuer: 'Default issuer',
        defaultSeries: 'Default series',
        numberingPrefix: 'Numbering prefix',
        nextNumber: 'Next number',
        paymentTermsDays: 'Payment terms (days)',
        defaultVatRate: 'Default VAT rate',
        pdfFooter: 'PDF footer',
        defaultTemplate: 'Default template',
        signatureFooter: 'Signature footer',
        xamppBasePath: 'XAMPP base path',
        defaultLocalDomainSuffix: 'Default local suffix',
        mysqlHost: 'MySQL host',
        mysqlPort: 'MySQL port',
        mysqlUser: 'MySQL user',
        mysqlPassword: 'MySQL password',
        mysqlCharset: 'MySQL charset',
        wordpressAdminUsername: 'WordPress admin user (default)',
        wordpressAdminPassword: 'WordPress admin password (default)',
        wordpressBaseThemePath: 'alttvision-base theme path',
        wordpressCorePluginPath: 'alttvision-core plugin path',
        oblioEnabled: 'Oblio enabled',
        eFacturaEnabled: 'eFactura enabled',
        oblioEmail: 'Oblio email',
        oblioApiToken: 'Oblio API token',
        oblioClientId: 'Oblio login email',
        oblioApiSecret: 'Oblio API secret',
        oblioIssuerCompanyName: 'Oblio company',
        oblioIssuerCif: 'Oblio CUI',
        oblioIssuerRegistrationNumber: 'Oblio registration no.',
        oblioInvoiceSeries: 'Oblio invoice series',
        oblioProformaSeries: 'Oblio proforma series',
        oblioDefaultVatName: 'Oblio VAT rate',
        oblioDefaultVatPercentage: 'Oblio VAT percent',
        eFacturaIssuerVat: 'eFactura issuer VAT',
        signingProvider: 'Signing provider',
        signingApiKey: 'Signing API key',
        paymentWebsiteBaseUrl: 'Public website URL',
        paymentApiBaseUrl: 'Payments API URL',
        paymentDefaultCurrency: 'Default payment currency',
        paymentDefaultExpirationDays: 'Default expiration (days)',
        stripeMode: 'Stripe mode',
        stripePublishableKey: 'Stripe publishable key',
        stripeSecretKey: 'Stripe secret key',
        stripeWebhookSecret: 'Stripe webhook secret',
        websiteApiToken: 'Website API token',
        aviProvider: 'AVI provider',
        aviModel: 'AVI model',
        projectContextEnabled: 'Project context enabled',
        aiDefaultProvider: 'Default provider',
        aiDefaultModel: 'Default model',
        aiTaskFullDesign: 'Model for full design',
        aiTaskEditSection: 'Model for section edits',
        aiTaskCopyPolish: 'Model for copy polish',
        aiCacheEnabled: 'AI response cache',
        aiAnthropicKey: 'Anthropic API key',
        aiOpenAiKey: 'OpenAI API key',
        locale: 'Interface language',
        navigationDensity: 'Navigation density',
        reduceMotion: 'Reduce motion'
      },
      actions: {
        save: 'Save',
        clearSecret: 'Clear',
        newCompanyProfile: 'New profile',
        saveCompanyProfile: 'Save profile',
        newCustomerCategory: 'New type',
        saveCustomerCategory: 'Save type',
        archiveCustomerCategory: 'Archive',
        unarchiveCustomerCategory: 'Unarchive',
        browseFolder: 'Browse...',
        validateBridge: 'Validate',
        refreshBridgeContract: 'Refresh contract',
        testOblio: 'Test Oblio',
        refreshOblio: 'Refresh series',
        saveAiKey: 'Save key',
        clearAiKey: 'Clear key',
        testAiKey: 'Test key',
        clearAiCache: 'Clear AI cache'
      },
      messages: {
        workspaceSaved: 'Workspace settings were saved.',
        localizationSaved: 'Localization settings were saved.',
        companyProfileSaved: 'Company profile was saved.',
        customerCategorySaved: 'Customer type was saved.',
        bridgeValidated: 'WordPress bridge validated.',
        bridgeContractRefreshed: 'WordPress bridge contract refreshed.',
        bridgeUnavailable: 'WordPress bridge is unavailable.',
        paymentsSaved: 'Payment settings were saved.',
        paymentSecretCleared: 'Payment secret removed.',
        aiKeySaved: 'API key stored securely.',
        aiKeyCleared: 'API key removed.',
        aiKeyTestOk: 'API key works. Latency: {latency} ms.',
        aiCacheCleared: '{count} entries cleared from cache.'
      },
      payments: {
        nestFlowNote: 'Live links sync to alttvision.com through the Website API token. Without the token, links stay local until you sync them.'
      },
      oblio: {
        connectionChecked: 'Oblio connection checked.',
        statusReady: 'Oblio configured',
        statusPending: 'Oblio unchecked',
        statusError: 'Oblio needs attention',
        company: 'Company',
        invoiceNext: 'Next invoice',
        proforma: 'Proforma',
        noProformaSeries: 'No Proforma series in Oblio'
      },
      customerTypes: {
        title: 'Customer types',
        preview: 'Preview',
        searchIcon: 'Search icon',
        untitled: 'Customer type'
      },
      options: {
        signingProvider: {
          none: 'No provider',
          reserved: 'Reserved'
        },
        provider: {
          reserved: 'Reserved'
        },
        aiTaskModel: {
          useDefault: 'Use default model'
        },
        stripeMode: {
          test: 'Test',
          live: 'Live'
        },
        navigationDensity: {
          compact: 'Compact',
          comfortable: 'Comfortable'
        }
      },
      wordpressPackages: {
        description: 'The bridge uses the theme and plugin manifests as the source of truth for the builder.',
        themeLabel: 'Theme (alttvision-base)',
        pluginLabel: 'Plugin (alttvision-core)',
        resolvedPath: 'Active path',
        bundledFallback: 'Bundled fallback',
        configuredPath: 'Configured path',
        packageVersion: 'Version {version}',
        packageNameUnknown: 'Manifest unavailable',
        bridgeStatusValid: 'Bridge valid',
        bridgeStatusInvalid: 'Bridge invalid',
        bridgeStatusUnknown: 'Status unknown',
        contractHash: 'Contract hash: {hash}',
        contractHashUnavailable: 'Contract hash unavailable',
        validatedAt: 'Validated at {timestamp}',
        noIssues: 'No issues reported.',
        issuesTitle: 'Detected issues',
        pickThemeDialogTitle: 'Select the alttvision-base theme directory',
        pickPluginDialogTitle: 'Select the alttvision-core plugin directory',
        bundledThemeFolder: 'Bundled theme',
        bundledPluginFolder: 'Bundled plugin',
        loading: 'Loading bridge status...'
      }
    },
    tasks: {
      title: 'Tasks',
      filters: {
        all: 'All',
        allLabels: 'All labels',
        allProjects: 'All projects'
      },
      search: {
        placeholder: 'Search tasks...'
      },
      sort: {
        recent: 'Most recent'
      },
      view: {
        kanban: 'Kanban',
        calendar: 'Calendar'
      },
      confirmDelete: 'Delete this task?',
      empty: {
        column: 'No tasks are visible in this column.'
      },
      fields: {
        status: 'Status'
      },
      card: {
        actions: {
          focus: 'Focus',
          edit: 'Edit',
          delete: 'Delete'
        }
      },
      modal: {
        createTitle: 'Add task',
        editTitle: 'Edit task',
        edit: 'Edit',
        dueAt: 'Due',
        labels: 'Labels',
        description: 'Description',
        noDescription: 'No description'
      },
      calendar: {
        title: 'Calendar',
        emptyTitle: 'The task calendar remains reserved in this milestone.',
        emptyDescription: 'We keep the same task entities and due-date groupings without shipping a full calendar workflow yet.',
        noDeadline: 'No deadline'
      }
    },
    resources: {
      summary: {
        subtitle: 'There are {total} files in {categories} categories'
      },
      search: {
        placeholder: 'Search files...'
      },
      projectModal: {
        title: 'Resources / Media',
        description: 'Media library for {projectName}. Files uploaded here can be used in Builder and AVI.',
        projectScope: 'Project only',
        loading: 'Loading project resources...',
        searchPlaceholder: 'Search project resources...',
        sortLabel: 'Sort',
        resultsCount: '{count} visible resources',
        emptyTitle: 'No resources in this project yet',
        emptyDescription: 'Upload images, icons, or documents so Builder and AVI can use them quickly.',
        inspectorTitle: 'Resource details',
        inspectorEmptyTitle: 'Select a resource',
        inspectorEmptyDescription: 'Choose an item from the library to preview and edit it.',
        confirmDeleteTitle: 'Delete this resource?',
        confirmDeleteMessage: 'The resource will be moved to Trash and will no longer be available to this project.',
        actions: {
          remove: 'Delete resource',
          confirmRemove: 'Confirm delete',
          removing: 'Removing...',
          saving: 'Saving...'
        },
        messages: {
          loadError: 'Project resources could not be loaded.',
          uploadError: 'Resource upload failed.',
          saveError: 'Resource save failed.',
          deleteError: 'Resource delete failed.'
        },
        notifications: {
          uploadSuccessTitle: 'Resources uploaded',
          uploadSuccessMessage: '{count} files were added to the project.',
          uploadErrorTitle: 'Upload failed',
          saveSuccessTitle: 'Resource updated',
          saveSuccessMessage: 'Resource details were saved locally.',
          saveErrorTitle: 'Resource update failed',
          deleteSuccessTitle: 'Resource deleted',
          deleteSuccessMessage: 'The resource was moved to Trash.',
          deleteErrorTitle: 'Resource delete failed'
        }
      },
      actions: {
        upload: 'Upload'
      },
      upload: {
        dropTitle: 'Drop files here',
        dropSubtitle: 'Release to upload to Resources',
        importing: 'Uploading...'
      },
      modal: {
        title: 'Resource details',
        notFound: 'Resource not found.',
        loadFailed: 'Resource unavailable.',
        saveFailed: 'Save failed.',
        validationName: 'Name is required.',
        updatedAt: 'Updated',
        fields: {
          altText: 'Alt text',
          description: 'Description'
        }
      },
      filters: {
        allProjects: 'All projects'
      },
      categories: {
        all: 'All',
        documents: 'Documents',
        images: 'Images',
        icons: 'Icons'
      },
      sort: {
        recent: 'Most recent',
        oldest: 'Oldest',
        nameAsc: 'Name A-Z',
        nameDesc: 'Name Z-A'
      },
      table: {
        name: 'Resource',
        kind: 'Type',
        project: 'Project',
        customer: 'Customer',
        updatedAt: 'Updated',
        status: 'Status'
      },
      selection: {
        title: '{count} resources selected',
        statusLabel: 'Status'
      },
      status: {
        active: 'Active',
        archived: 'Archived'
      }
    },
    icons: {
      business: {
        badgeCheck: 'Verified',
        briefcaseBusiness: 'Business services',
        briefcaseMedical: 'Medical services',
        building2: 'Company',
        car: 'Automotive',
        graduationCap: 'Education',
        hammer: 'Construction',
        heartPulse: 'Health',
        hospital: 'Clinic',
        house: 'Real estate',
        landmark: 'Institution',
        monitorCog: 'Technology',
        paintbrush: 'Design',
        pill: 'Pharmacy',
        scale: 'Legal',
        scissors: 'Beauty',
        shoppingBag: 'E-commerce',
        sparkles: 'Premium',
        sprayCan: 'Cleaning',
        store: 'Retail',
        stethoscope: 'Doctor',
        truck: 'Transport',
        users: 'Community',
        utensils: 'HoReCa',
        wrench: 'Service'
      }
    },
    enums: {
      projectStatus: {
        active: 'Active',
        in_progress: 'In progress',
        pending: 'Pending',
        complete: 'Complete',
        archived: 'Archived'
      },
      projectStage: {
        brief: 'Brief',
        branding: 'Branding',
        build: 'Build',
        review: 'Review',
        launch: 'Launch',
        support: 'Support'
      },
      deploymentState: {
        idle: 'Idle',
        ready: 'Ready',
        updating: 'Updating',
        warning: 'Warning'
      },
      leadStatus: {
        new: 'New',
        to_call: 'To call',
        contacted: 'Contacted',
        follow_up: 'Follow-up',
        qualified: 'Qualified',
        converted: 'Converted',
        lost: 'Lost'
      },
      leadActivityKind: {
        call: 'Call',
        email: 'Email',
        whatsapp: 'WhatsApp',
        meeting: 'Meeting',
        note: 'Note',
        status_change: 'Status change'
      },
      leadActivityOutcome: {
        reached: 'Reached',
        no_answer: 'No answer',
        left_message: 'Left message',
        interested: 'Interested',
        not_interested: 'Not interested',
        scheduled_follow_up: 'Scheduled follow-up',
        qualified: 'Qualified',
        converted: 'Converted',
        lost: 'Lost',
        note: 'Note'
      },
      customerType: {
        company: 'Company',
        sole_trader: 'Sole trader',
        individual: 'Individual'
      },
      customerStatus: {
        active: 'Active',
        inactive: 'Inactive',
        lead: 'Lead',
        archived: 'Archived'
      },
      customerContractStatus: {
        draft: 'Draft',
        active: 'Active',
        signed: 'Signed',
        archived: 'Archived'
      },
      customerInvoiceStatus: {
        draft: 'Draft',
        issued: 'Issued',
        paid: 'Paid',
        overdue: 'Overdue',
        archived: 'Archived'
      },
      contractStatus: {
        draft: 'Draft',
        pending_review: 'In review',
        ready_to_send: 'Ready to send',
        sent: 'Sent',
        signed: 'Signed',
        expired: 'Expired',
        archived: 'Archived'
      },
      contractTemplateStatus: {
        draft: 'Draft',
        active: 'Active',
        archived: 'Archived'
      },
      contractTemplateCategory: {
        service_agreement: 'Service agreement',
        website_development: 'Website development',
        maintenance: 'Maintenance',
        marketing: 'Marketing',
        hosting: 'Hosting',
        consulting: 'Consulting',
        nda: 'NDA',
        annex: 'Annex'
      },
      contractSignatureStatus: {
        not_started: 'Not started',
        pending: 'Pending',
        signed: 'Signed',
        not_required: 'Not required'
      },
      invoiceStatus: {
        draft: 'Draft',
        issued: 'Issued',
        sent: 'Sent',
        paid: 'Paid',
        partially_paid: 'Partially paid',
        overdue: 'Overdue',
        archived: 'Archived'
      },
      invoiceSyncStatus: {
        not_configured: 'Not configured',
        pending: 'Pending',
        synced: 'Synced',
        error: 'Error'
      },
      paymentStatus: {
        draft: 'Draft',
        ready: 'Ready',
        sent: 'Sent',
        viewed: 'Viewed',
        accepted: 'Accepted',
        checkout_started: 'Checkout started',
        paid: 'Paid',
        payment_failed: 'Payment failed',
        cancelled: 'Cancelled',
        expired: 'Expired',
        archived: 'Archived'
      },
      paymentSyncStatus: {
        not_configured: 'Not configured',
        local_only: 'Local only',
        pending: 'Pending',
        synced: 'Synced',
        error: 'Error'
      },
      taskStatus: {
        backlog: 'Backlog',
        todo: 'To do',
        in_progress: 'In progress',
        done: 'Done',
        archived: 'Archived'
      },
      taskPriority: {
        low: 'Low',
        medium: 'Medium',
        high: 'High'
      },
      assetKind: {
        logo: 'Logo',
        icon: 'Icon',
        document: 'Document',
        image: 'Image',
        pdf: 'PDF'
      },
      builderPageStatus: {
        draft: 'Draft',
        ready: 'Ready',
        archived: 'Archived'
      }
    },
    placeholder: {
      title: '{label} will be defined after foundation.',
      description: 'Milestone 00 reserves the route, navigation slot, and layout without inventing the full workflow for this domain.'
    }
  }
} as const;

function getMessageValue(locale: keyof typeof messages, key: string): string | undefined {
  return key.split('.').reduce<unknown>((value, part) => {
    if (!value || typeof value === 'string') {
      return undefined;
    }

    return (value as Record<string, unknown>)[part];
  }, messages[locale]) as string | undefined;
}

export function translate(locale: keyof typeof messages, key: string, values?: Record<string, string | number>): string {
  const message = getMessageValue(locale, key) ?? getMessageValue('ro-RO', key) ?? key;

  if (!values) {
    return message;
  }

  return Object.entries(values).reduce(
    (result, [entryKey, entryValue]) => result.replaceAll(`{${entryKey}}`, String(entryValue)),
    message
  );
}

export function useI18n() {
  const locale = useShellStore((state) => state.settings.locale);

  return {
    locale,
    t: useCallback((key: string, values?: Record<string, string | number>) => translate(locale, key, values), [locale])
  };
}
